import { ApolloError, useMutation } from '@apollo/client';
import classNames from 'classnames';
import isEqual from 'lodash/isEqual';
import { Button } from 'components/Button';
import { NonNegativeNumberInput } from 'components/NonNegativeNumberInput';
import { withNotification } from 'components/Notification';
import SettingsViewWrapper from 'components/SettingsWrapperView';
import { Toggle } from 'components/Toggle';
import { UnsavedChangesGuard } from 'components/UnsavedChangesGuard';
import { useStore } from 'context/store-context';
import { SAVE_RETURN_WORKFLOW_SETTINGS } from 'gql/mutations';
import { ReactNode, useEffect, useState, useMemo, useRef } from 'react';
import { apiErrorHandler, NotificationTypes, SizeEnum } from 'utils';
import {
  InformationCircleIcon,
  SupportIcon,
  CalendarIcon,
  BanIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  ShieldCheckIcon,
  BookOpenIcon,
  PlusIcon,
  TrashIcon,
} from '@heroicons/react/outline';
import {
  PolicySettings as PolicySettingsShape,
  DatedProductRule,
  ApprovalRules,
  PolicyStrictnessMode,
  PolicyTimeTrigger,
  ShipmentDeadlineStartsAt,
  SettlementTimingTrigger,
  ReturnApprovalMode,
  SettlementTimingRule,
  SettlementTimingRules,
  SettlementTimingBranch,
  POLICY_MODE_LABELS,
  getSettlementTimingRules,
  getApprovalRules,
  getPolicyModeBehavior,
  getPolicyModeApprovalPreset,
  getPolicyModeRulePreset,
  getRequestApprovalSettings,
  RequestApprovalMode,
  RequestApprovalSettings,
  isPolicyApprovalPresetCustomized,
  isPolicyRulePresetCustomized,
} from 'types/policy';
import ReturnsSettingsTabs from './ReturnsSettingsTabs';
import { defaultInspectionPolicy, getInspectionPolicyError, InspectionPolicyControls } from './InspectionPolicyControls';
import { getPolicyPageSaveInput, PolicySettingsPage } from './PolicySettings.pages';
import { DefectResolutionPolicy } from './DefectResolutionPolicy';
import ReturnsProgramStatus from './ReturnsProgramStatus';
import SharedResolutionSettingsNotice from './SharedResolutionSettingsNotice';
import { CustomerRestrictionsPolicy } from './CustomerRestrictionsPolicy';
import { useReturnCustomerRestrictions } from './useReturnCustomerRestrictions';
import { getReturnReasonSummary } from './PolicySettings.reasonSummary';
import {
  normalizeReturnEvidenceRequirements,
  ReturnEvidenceDefault,
  ReturnEvidenceField,
  ReturnEvidenceOverrideValue,
  ReturnEvidenceRequirements,
  updateReasonEvidenceOverride,
} from './PolicySettings.returnRequirements';
import {
  getPolicySettingsValidationError,
  parseShipmentReminderDays,
} from './PolicySettings.validation';
import ProductEligibilityControls, {
  ProductEligibilityActivationState,
} from './ProductEligibilityControls';
import {
  getProductEligibilityFingerprint,
  hasConfiguredProductEligibilityRule,
  normalizeProductEligibilitySettings,
  ProductEligibilitySettingsInput,
  PRODUCT_RULES_VERSION,
} from './PolicySettings.productEligibility';
import {
  describeDatedProductRule,
  getDatedProductRulesError,
  normalizeDatedProductRules,
} from './PolicySettings.datedRules';
import useExchangeProcessingPolicy, {EXCHANGE_PROCESSING_TRIGGERS, ExchangeProcessingTrigger} from './useExchangeProcessingPolicy';
import {ReviewProcessingWorkflow} from './ReviewProcessingWorkflow';
import {getReviewExchangeCompatibility, getReplacementWorkflowSummary} from './reviewExchangeCompatibility';
import {completeInspectionPolicy, copyKnownInspectionShape, getInspectionAuthority, hasCanonicalAllReturnsInspection, inspectionAuthorityVerified, InspectionAuthority, sameInspectionAuthority} from './reviewInspectionAuthority';

const WINDOW_TRIGGER_LABELS: Record<PolicyTimeTrigger, string> = {
  [PolicyTimeTrigger.DELIVERY_DATE]: 'delivery date',
  [PolicyTimeTrigger.ORDER_DATE]: 'order date',
  [PolicyTimeTrigger.FULFILLMENT_DATE]: 'fulfillment date',
};

const APPROVAL_MODE_OPTIONS: Array<{
  value: ReturnApprovalMode;
  label: string;
  description: string;
}> = [
  {
    value: ReturnApprovalMode.AUTO_APPROVE,
    label: 'Approve automatically',
    description: 'Approve immediately without team review.',
  },
  {
    value: ReturnApprovalMode.MANUAL_REVIEW,
    label: 'Send to review',
    description: 'Hold for a team member to approve or deny.',
  },
  {
    value: ReturnApprovalMode.AUTO_DENY,
    label: 'Deny automatically',
    description: 'Deny immediately without team review.',
  },
];

const getApprovalModeOption = (mode: ReturnApprovalMode) =>
  APPROVAL_MODE_OPTIONS.find(({ value }) => value === mode) ||
  APPROVAL_MODE_OPTIONS[1];

const getPolicyModePresetSummary = (mode: PolicyStrictnessMode) => {
  const preset = getPolicyModeApprovalPreset(mode);

  return `Pass: ${
    getApprovalModeOption(preset.eligibleApprovalMode).label
  } · Flagged: ${getApprovalModeOption(preset.flaggedApprovalMode).label}`;
};

const SETTLEMENT_TIMING_OPTIONS: Array<{
  value: SettlementTimingTrigger;
  label: string;
  description: string;
}> = [
  {
    value: 'APPROVAL',
    label: 'After request approval',
    description: 'Issue the refund or credit once the request is approved and any required inspection has passed.',
  },
  {
    value: 'FIRST_CARRIER_SCAN',
    label: 'After first carrier scan',
    description: 'Issue funds once the carrier scans the return shipment.',
  },
  {
    value: 'INBOUND_DELIVERED',
    label: 'After carrier confirms return delivery',
    description: 'Issue funds when tracking shows the return was delivered.',
  },
  {
    value: 'WAREHOUSE_RECEIVED',
    label: 'After team confirms receipt',
    description: 'Team confirms returned items are received. Any required inspection must also pass.',
  },
  {
    value: 'INSPECTION_APPROVED',
    label: 'After inspection approval',
    description: 'Issue funds after inspection is approved.',
  },
];

const getSettlementTimingOption = (trigger: SettlementTimingTrigger) =>
  SETTLEMENT_TIMING_OPTIONS.find(({ value }) => value === trigger) ||
  SETTLEMENT_TIMING_OPTIONS[3];


const RETURN_REASON_OPTIONS = [
  {
    key: 'WRONG_ITEM',
    title: 'Wrong Item',
    description: 'Customer received an item that does not match the order.',
    category: 'Product issue',
  },
  {
    key: 'DAMAGED',
    title: 'Damaged or Defective',
    description: 'Item arrived damaged, broken, or not working as expected.',
    category: 'Product issue',
  },
  {
    key: 'NOT_AS_DESCRIBED',
    title: 'Not as Described',
    description:
      'Item looks, feels, or performs differently than the product page suggested.',
    category: 'Product issue',
  },
  {
    key: 'MISSING_ITEM_OR_PARTS',
    title: 'Missing Item or Parts',
    description: 'The order is incomplete or pieces are missing from the box.',
    category: 'Product issue',
  },
  {
    key: 'DOES_NOT_FIT',
    title: "Doesn't Fit",
    description: 'Size, fit, or measurements are not right for the customer.',
    category: 'Fit or preference',
  },
  {
    key: 'CHANGED_MIND',
    title: 'Changed Mind',
    description: 'Customer no longer wants the item after delivery.',
    category: 'Fit or preference',
  },
  {
    key: 'ARRIVED_LATE',
    title: 'Arrived Late',
    description: 'The order arrived too late to be useful for the customer.',
    category: 'Delivery issue',
  },
  {
    key: 'OTHER',
    title: 'Other',
    description:
      'Catch-all reason for returns that do not fit the main options.',
    category: 'Catch-all',
  },
] as const;

type PresetReturnReason = (typeof RETURN_REASON_OPTIONS)[number];
type PresetReturnReasonKey = PresetReturnReason['key'];
type CustomReturnReason = {
  id: string;
  label: string;
  analyticsKey: string;
};

const RETURN_REASON_GROUPS: Array<{
  title: string;
  description: string;
  keys: PresetReturnReasonKey[];
}> = [
  {
    title: 'Product issues',
    description:
      'Use these when the item, condition, or contents are the problem.',
    keys: [
      'WRONG_ITEM',
      'DAMAGED',
      'NOT_AS_DESCRIBED',
      'MISSING_ITEM_OR_PARTS',
    ],
  },
  {
    title: 'Fit and preference',
    description:
      'Use these when the customer no longer wants to keep the item.',
    keys: ['DOES_NOT_FIT', 'CHANGED_MIND'],
  },
  {
    title: 'Delivery and catch-all',
    description:
      'Keep a broad option available for unusual cases or timing issues.',
    keys: ['ARRIVED_LATE', 'OTHER'],
  },
];

const DEFAULT_ENABLED_RETURN_REASONS = [
  'WRONG_ITEM',
  'DAMAGED',
  'CHANGED_MIND',
  'OTHER',
] as PresetReturnReasonKey[];

const DEFAULT_RETURN_WORKFLOW_SETTINGS: Required<
  Pick<
    PolicySettingsShape,
    'shipmentDeadline' | 'refundTiming' | 'fraudRules' | 'reasonRules'
  >
> = {
  shipmentDeadline: {
    days: 14,
    startsAt: 'LABEL_READY',
    reminderDaysBeforeDeadline: [10, 2, 0],
    expireAutomatically: true,
  },
  refundTiming: {
    trigger: 'WAREHOUSE_RECEIVED',
    delayDays: 0,
    appliesTo: ['REFUND', 'STORE_CREDIT'],
    skipAutomationWhenFlagged: true,
  },
  fraudRules: {},
  reasonRules: {
    damagedRequiresReview: true,
    damagedRequiresPhotos: false,
    damagedNotifiesMerchant: true,
  },
};

const DEFAULT_CUSTOMER_RETURN_EXPERIENCE = {
  returnInstructions:
    'Pack the items securely and include all original accessories. Attach the return label to the outside of the package before dropping it off with the carrier.',
};

const DEFAULT_RETURN_EVIDENCE_REQUIREMENTS =
  normalizeReturnEvidenceRequirements({});

const RETURN_EVIDENCE_DEFAULT_OPTIONS: Array<{
  value: ReturnEvidenceDefault;
  label: string;
}> = [
  { value: 'REASON_BASED', label: 'Recommended by reason' },
  { value: 'HIDDEN', label: "Don't ask" },
  { value: 'OPTIONAL', label: 'Optional for all' },
  { value: 'REQUIRED', label: 'Required for all' },
];

const RETURN_EVIDENCE_OVERRIDE_OPTIONS: Array<{
  value: ReturnEvidenceOverrideValue;
  label: string;
}> = [
  { value: 'INHERIT', label: 'Use default' },
  { value: 'HIDDEN', label: "Don't ask" },
  { value: 'OPTIONAL', label: 'Optional' },
  { value: 'REQUIRED', label: 'Required' },
];

const RETURN_EVIDENCE_DEFAULT_SUMMARY: Record<ReturnEvidenceDefault, string> = {
  REASON_BASED: 'recommended by reason',
  HIDDEN: 'not requested',
  OPTIONAL: 'optional for all',
  REQUIRED: 'required for all',
};

const normalizeReturnReasonLabel = (value: string) =>
  value.trim().replace(/\s+/g, ' ');

const slugifyReturnReason = (value: string) => {
  const normalized = normalizeReturnReasonLabel(value)
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');

  return normalized || 'reason';
};

const buildUniqueCustomAnalyticsKey = (
  label: string,
  existingKeys: string[],
) => {
  const base = `custom_${slugifyReturnReason(label)}`;
  let nextKey = base;
  let suffix = 2;

  while (existingKeys.includes(nextKey)) {
    nextKey = `${base}_${suffix}`;
    suffix += 1;
  }

  return nextKey;
};

const normalizeCustomReturnReasons = (reasons: any): CustomReturnReason[] => {
  if (!Array.isArray(reasons)) {
    return [];
  }

  const normalizedReasons: CustomReturnReason[] = [];
  const usedReasonIds = new Set<string>();

  reasons.forEach((reason) => {
    const label = normalizeReturnReasonLabel(
      reason?.label || reason?.title || reason?.name || '',
    );

    if (!label) {
      return;
    }

    const analyticsKey = normalizeReturnReasonLabel(reason?.analyticsKey || '')
      ? String(reason.analyticsKey).trim()
      : buildUniqueCustomAnalyticsKey(
          label,
          normalizedReasons.map(({ analyticsKey }) => analyticsKey),
        );

    const baseId =
      typeof reason?.id === 'string' && reason.id.trim().length > 0
        ? reason.id.trim()
        : `CUSTOM_${analyticsKey.toUpperCase()}`;
    let nextId = baseId;
    let suffix = 2;

    while (usedReasonIds.has(nextId)) {
      nextId = `${baseId}_${suffix}`;
      suffix += 1;
    }

    usedReasonIds.add(nextId);
    normalizedReasons.push({
      id: nextId,
      label,
      analyticsKey,
    });
  });

  return normalizedReasons;
};

type CollapsiblePolicySectionProps = {
  id: string;
  title: string;
  description?: string;
  summary?: string;
  icon?: ReactNode;
  badge?: ReactNode;
  isExpanded: boolean;
  onToggle: () => void;
  children: ReactNode;
  className?: string;
};

const CollapsiblePolicySection = ({
  id,
  title,
  description,
  summary,
  icon,
  badge,
  isExpanded,
  onToggle,
  children,
  className,
}: CollapsiblePolicySectionProps) => {
  const supportingText = isExpanded ? description : summary || description;

  return (
    <section
      className={classNames(
        'bg-white rounded-xl border border-gray-200 shadow-sm',
        isExpanded ? 'overflow-visible' : 'overflow-hidden',
        className,
      )}
    >
      <div className="flex w-full items-start justify-between gap-4 transition-colors hover:bg-gray-50">
        <button
          type="button"
          onClick={onToggle}
          aria-expanded={isExpanded}
          aria-controls={`${id}-panel`}
          className="flex min-w-0 flex-1 items-start gap-3 px-5 py-4 text-left"
        >
          {icon && (
            <span className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-gray-50 text-gray-500">
              {icon}
            </span>
          )}
          <div className="min-w-0">
            <h3 className="text-base font-semibold text-gray-900">{title}</h3>
            {supportingText && (
              <p className="mt-1 max-w-2xl text-sm leading-5 text-gray-500">
                {supportingText}
              </p>
            )}
          </div>
        </button>
        <div className="flex shrink-0 items-center gap-3 py-4 pr-5">
          {badge}
          <button
            type="button"
            onClick={onToggle}
            aria-expanded={isExpanded}
            aria-controls={`${id}-panel`}
            aria-label={`${isExpanded ? 'Collapse' : 'Expand'} ${title}`}
            className="rounded-md p-1 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600"
          >
            {isExpanded ? (
              <ChevronDownIcon className="h-5 w-5" />
            ) : (
              <ChevronRightIcon className="h-5 w-5" />
            )}
          </button>
        </div>
      </div>
      {isExpanded && (
        <div id={`${id}-panel`} className="border-t border-gray-100">
          {children}
        </div>
      )}
    </section>
  );
};

const SettingsSectionGroup = ({
  visible,
  children,
}: {
  visible: boolean;
  children: ReactNode;
}) => (visible ? <>{children}</> : null);

type SettlementTimingCardProps = {
  id: string;
  title: string;
  description: string;
  rule: SettlementTimingRule;
  onChange: (rule: SettlementTimingRule) => void;
  inspectionRequired?: boolean;
  accessibleLabel?: string;
  outcome: 'refund' | 'storeCredit';
};

const SettlementTimingCard = ({
  id,
  title,
  description,
  rule,
  onChange,
  inspectionRequired = false,
  accessibleLabel,
  outcome,
}: SettlementTimingCardProps) => {
  const selectedTiming = getSettlementTimingOption(rule.trigger);

  return (
    <div className="grid gap-x-5 rounded-xl border border-gray-200 bg-gray-50 p-3 sm:grid-cols-[minmax(0,1fr)_minmax(0,1.5fr)] sm:p-4">
      <div>
        <h4 className="text-sm font-semibold text-gray-900">{title}</h4>
        <p className="mt-1 text-xs leading-5 text-gray-500">{description}</p>
      </div>

      <label className="mt-3 block sm:mt-0" htmlFor={`${id}-trigger`}>
        <span className="text-xs font-semibold text-gray-700">{outcome === 'refund' ? 'Issue refund after' : 'Issue credit after'}</span>
        <select
          id={`${id}-trigger`}
          aria-label={accessibleLabel ? `${accessibleLabel} timing` : undefined}
          value={rule.trigger}
          onChange={(event) =>
            onChange({
              ...rule,
              trigger: event.target.value as SettlementTimingTrigger,
            })
          }
          className="mt-1.5 block w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-900 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
        >
          {SETTLEMENT_TIMING_OPTIONS.filter(option => inspectionRequired || option.value !== 'INSPECTION_APPROVED').map(({ value, label }) => (
            <option key={value} value={value}>
              {label}
            </option>
          ))}
        </select>
        <span className="mt-2 block text-xs leading-5 text-gray-500">
          {selectedTiming.description}
        </span>
      </label>

      <label className="mt-3 block sm:col-start-2" htmlFor={`${id}-delay`}>
        <span className="text-xs font-semibold text-gray-700">
          Delay after event
        </span>
        <div className="relative mt-1.5">
          <NonNegativeNumberInput
            id={`${id}-delay`}
            aria-label={accessibleLabel ? `${accessibleLabel} delay (days)` : undefined}
            min={0}
            max={90}
            value={rule.delayDays}
            onChange={(event) =>
              onChange({
                ...rule,
                delayDays: Math.max(
                  0,
                  Math.min(90, Number(event.target.value) || 0),
                ),
              })
            }
            className="block w-full rounded-lg border border-gray-300 bg-white py-2 pl-3 pr-16 text-sm text-gray-900 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
          />
          <span className="pointer-events-none absolute inset-y-0 right-3 flex items-center text-xs font-medium text-gray-500">
            days
          </span>
        </div>
      </label>
    </div>
  );
};

const PolicySettings = withNotification(
  ({
    showNotification,
    settingsPage = 'policy',
  }: {
    showNotification: any;
    settingsPage?: PolicySettingsPage;
  }) => {
    const isApprovalSettings = settingsPage === 'approval';
    const isSharedPolicy = settingsPage === 'policy';
    const isReturnsSettings = settingsPage === 'returns';
    const { storeId, storeProperties, refetchStoreProperties } = useStore();
    const exchangeProcessing = useExchangeProcessingPolicy(storeId, isApprovalSettings);
    const customerRestrictions = useReturnCustomerRestrictions(
      storeId,
      isSharedPolicy,
    );
    const [
      saveReturnWorkflowSettings,
      { loading: saveReturnWorkflowSettingsLoading },
    ] = useMutation(SAVE_RETURN_WORKFLOW_SETTINGS);
    // Form state
    const [returnWindow, setReturnWindow] = useState(30);
    const [windowStart, setWindowStart] = useState<PolicyTimeTrigger>(
      PolicyTimeTrigger.DELIVERY_DATE,
    );
    const [allowUndelivered, setAllowUndelivered] = useState(false);
    const [shipmentDeadlineDays, setShipmentDeadlineDays] = useState(14);
    const [shipmentDeadlineStartsAt, setShipmentDeadlineStartsAt] =
      useState<ShipmentDeadlineStartsAt>('LABEL_READY');
    const [shipmentReminderDaysInput, setShipmentReminderDaysInput] =
      useState('10, 2, 0');
    const [expireReturnsAutomatically, setExpireReturnsAutomatically] =
      useState(true);

    // Eligibility & Exclusions
    const [productRulesVersion, setProductRulesVersion] = useState<
      number | undefined
    >();
    const [productRulesEnabled, setProductRulesEnabled] = useState(false);
    const [
      persistedProductEligibilityFingerprint,
      setPersistedProductEligibilityFingerprint,
    ] = useState(() => getProductEligibilityFingerprint(undefined));
    const [
      requiresProductRulesReconfirmation,
      setRequiresProductRulesReconfirmation,
    ] = useState(false);
    const [productRulesEnabledAt, setProductRulesEnabledAt] = useState<
      string | undefined
    >();
    const [finalSaleEnforced, setFinalSaleEnforced] = useState(false);
    const [excludedTags, setExcludedTags] = useState<string[]>([]);
    const [excludedCollections, setExcludedCollections] = useState<string[]>(
      [],
    );
    const [excludedSkus, setExcludedSkus] = useState<string[]>([]);
    const [datedRules, setDatedRules] = useState<DatedProductRule[]>([]);
    const [datedRulesLoadError, setDatedRulesLoadError] = useState<string | null>(
      null,
    );
    const [excludedCollectionLabels, setExcludedCollectionLabels] = useState<
      Record<string, string>
    >({});
    const [
      productEligibilityActivationState,
      setProductEligibilityActivationState,
    ] = useState<ProductEligibilityActivationState | null>(null);
    const [enabledReturnReasons, setEnabledReturnReasons] = useState<string[]>([
      ...DEFAULT_ENABLED_RETURN_REASONS,
    ]);
    const [customReturnReasons, setCustomReturnReasons] = useState<
      CustomReturnReason[]
    >([]);
    const [newCustomReturnReason, setNewCustomReturnReason] = useState('');
    const [expandedPolicySections, setExpandedPolicySections] = useState<
      Record<string, boolean>
    >({
      policyMode: true,
      settlementTiming: true,
      returnWindow: true,
      shipmentDeadline: true,
      exclusions: true,
      returnReasons: true,
      customerExperience: true,
      policySummary: true,
      policyGenerator: true,
    });

    // Strictness
    const [policyMode, setPolicyMode] = useState<PolicyStrictnessMode>(
      PolicyStrictnessMode.CUSTOMER_FIRST,
    );
    const [eligibleApprovalMode, setEligibleApprovalMode] =
      useState<ReturnApprovalMode>(ReturnApprovalMode.AUTO_APPROVE);
    const [flaggedApprovalMode, setFlaggedApprovalMode] =
      useState<ReturnApprovalMode>(ReturnApprovalMode.MANUAL_REVIEW);
    const [requestApproval, setRequestApproval] = useState<RequestApprovalSettings>({refund: 'AUTOMATIC', exchange: 'AUTOMATIC'});
    const [approvalTouched, setApprovalTouched] = useState(false);
    const [approvalInvalid, setApprovalInvalid] = useState(false);
    const approvalOwner = useRef({storeId}), activeSave = useRef<object | null>(null), approvalMounted = useRef(true);
    if (approvalOwner.current.storeId !== storeId) {approvalOwner.current = {storeId};activeSave.current = null;}
    const approvalScope = approvalOwner.current;
    const ownsApprovalScope = () => approvalMounted.current && approvalOwner.current === approvalScope;
    useEffect(() => () => {approvalMounted.current = false;activeSave.current = null;}, []);
    const [approvalRules, setApprovalRules] = useState<ApprovalRules>(() =>
      getPolicyModeRulePreset(PolicyStrictnessMode.CUSTOMER_FIRST),
    );
    const [priorReturnsBaseline, setPriorReturnsBaseline] = useState(() => getPolicyModeRulePreset(PolicyStrictnessMode.CUSTOMER_FIRST).maxPriorReturns);
    const [priorReturnsTouched, setPriorReturnsTouched] = useState(false);
    const [settlementTimingRules, setSettlementTimingRules] =
      useState<SettlementTimingRules>(() => getSettlementTimingRules());
    const [inspectionPolicy, setInspectionPolicy] = useState(() => defaultInspectionPolicy());
    const [inspectionBaseline, setInspectionBaseline] = useState(() => defaultInspectionPolicy());
    const [inspectionSourceBaseline, setInspectionSourceBaseline] = useState(() => getInspectionAuthority(undefined));
    const [inspectionRepairRequested, setInspectionRepairRequested] = useState(false);
    const [inspectionExplicitlyChosen, setInspectionExplicitlyChosen] = useState(false);
    const [inspectionAcknowledgement, setInspectionAcknowledgement] = useState<{scope: object; beforePolicy: any; authority: InspectionAuthority; policySettings: any} | null>(null);
    const [workflowVerificationError, setWorkflowVerificationError] = useState<string | null>(null);
    const [partialSaveMessage, setPartialSaveMessage] = useState<string | null>(null);
    const inspectionEnabled = inspectionPolicy.enabled;

    const [returnEvidenceDefaults, setReturnEvidenceDefaults] = useState<
      ReturnEvidenceRequirements['defaults']
    >({ ...DEFAULT_RETURN_EVIDENCE_REQUIREMENTS.defaults });
    const [returnEvidenceOverrides, setReturnEvidenceOverrides] = useState<
      ReturnEvidenceRequirements['byReason']
    >({ ...DEFAULT_RETURN_EVIDENCE_REQUIREMENTS.byReason });
    const [showReasonRequirementOverrides, setShowReasonRequirementOverrides] =
      useState(true);
    const [returnInstructions, setReturnInstructions] = useState(
      DEFAULT_CUSTOMER_RETURN_EXPERIENCE.returnInstructions,
    );
    const [saveStatus, setSaveStatus] = useState<
      'idle' | 'saved' | 'error' | 'partial'
    >('idle');
    const [policySavePending, setPolicySavePending] = useState(false);
    const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
    const [defectPolicyDirty, setDefectPolicyDirty] = useState(false);
    const [defectPolicySaving, setDefectPolicySaving] = useState(false);
    const defectPolicySave = useRef<(() => Promise<boolean>) | null>(null);
    const hydratedStoreIdRef = useRef<string | null>(null);

    const markUnsavedChanges = () => {
      setHasUnsavedChanges(true);
      setSaveStatus('idle');
    };

    const handleReturnWindowChange = (value: number) => {
      markUnsavedChanges();
      setReturnWindow(value);
    };

    const handleWindowStartChange = (value: PolicyTimeTrigger) => {
      markUnsavedChanges();
      setWindowStart(value);
    };

    const handleReturnInstructionsChange = (value: string) => {
      markUnsavedChanges();
      setReturnInstructions(value);
    };

    const handleProductRulesEnabledChange = (enabled: boolean) => {
      markUnsavedChanges();
      setProductRulesVersion(PRODUCT_RULES_VERSION);
      setProductRulesEnabled(enabled);
      setProductRulesEnabledAt(enabled ? new Date().toISOString() : undefined);
    };

    const handleFinalSaleEnforcedChange = (enabled: boolean) => {
      markUnsavedChanges();
      setFinalSaleEnforced(enabled);
    };

    const handleDatedRulesChange = (rules: DatedProductRule[]) => {
      markUnsavedChanges();
      setDatedRules(rules);
      setDatedRulesLoadError(null);
    };

    const handleProductExclusionsChange = ({
      excludedTags: nextExcludedTags,
      excludedCollections: nextExcludedCollections,
      excludedSkus: nextExcludedSkus,
      excludedCollectionLabels: nextExcludedCollectionLabels,
    }: {
      excludedTags: string[];
      excludedCollections: string[];
      excludedSkus: string[];
      excludedCollectionLabels: Record<string, string>;
    }) => {
      markUnsavedChanges();
      setExcludedTags(nextExcludedTags);
      setExcludedCollections(nextExcludedCollections);
      setExcludedSkus(nextExcludedSkus);
      setExcludedCollectionLabels(nextExcludedCollectionLabels);
    };

    useEffect(() => {
      if (storeId && hydratedStoreIdRef.current === storeId) {
        return;
      }

      hydratedStoreIdRef.current = null;
      const defaultMode = PolicyStrictnessMode.CUSTOMER_FIRST;
      const defaultApprovalModes = getPolicyModeApprovalPreset(defaultMode);

      setReturnWindow(30);
      setInspectionPolicy(defaultInspectionPolicy());
      setInspectionBaseline(defaultInspectionPolicy());
      setInspectionSourceBaseline(getInspectionAuthority(undefined));
      setInspectionRepairRequested(false);
      setInspectionExplicitlyChosen(false);
      setInspectionAcknowledgement(null);
      setWorkflowVerificationError(null);
      setWindowStart(PolicyTimeTrigger.DELIVERY_DATE);
      setAllowUndelivered(false);
      setShipmentDeadlineDays(
        DEFAULT_RETURN_WORKFLOW_SETTINGS.shipmentDeadline.days,
      );
      setShipmentDeadlineStartsAt(
        DEFAULT_RETURN_WORKFLOW_SETTINGS.shipmentDeadline.startsAt,
      );
      setShipmentReminderDaysInput(
        DEFAULT_RETURN_WORKFLOW_SETTINGS.shipmentDeadline.reminderDaysBeforeDeadline.join(
          ', ',
        ),
      );
      setExpireReturnsAutomatically(
        DEFAULT_RETURN_WORKFLOW_SETTINGS.shipmentDeadline.expireAutomatically,
      );
      setProductRulesVersion(undefined);
      setProductRulesEnabled(false);
      setPersistedProductEligibilityFingerprint(
        getProductEligibilityFingerprint(undefined),
      );
      setRequiresProductRulesReconfirmation(false);
      setProductRulesEnabledAt(undefined);
      setFinalSaleEnforced(false);
      setExcludedTags([]);
      setExcludedCollections([]);
      setExcludedSkus([]);
      setDatedRules([]);
      setDatedRulesLoadError(null);
      setExcludedCollectionLabels({});
      setEnabledReturnReasons([...DEFAULT_ENABLED_RETURN_REASONS]);
      setCustomReturnReasons([]);
      setNewCustomReturnReason('');
      setPolicyMode(defaultMode);
      setEligibleApprovalMode(defaultApprovalModes.eligibleApprovalMode);
      setFlaggedApprovalMode(defaultApprovalModes.flaggedApprovalMode);
      setRequestApproval(getRequestApprovalSettings()!);
      setApprovalTouched(false);
      setApprovalInvalid(false);
      setApprovalRules(getPolicyModeRulePreset(defaultMode));
      setPriorReturnsBaseline(getPolicyModeRulePreset(defaultMode).maxPriorReturns);
      setPriorReturnsTouched(false);
      setSettlementTimingRules(getSettlementTimingRules());
      setReturnEvidenceDefaults({
        ...DEFAULT_RETURN_EVIDENCE_REQUIREMENTS.defaults,
      });
      setReturnEvidenceOverrides({});
      setShowReasonRequirementOverrides(true);
      setReturnInstructions(
        DEFAULT_CUSTOMER_RETURN_EXPERIENCE.returnInstructions,
      );
      setHasUnsavedChanges(false);
      setPolicySavePending(false);
      setSaveStatus('idle');

      if (!storeId || !storeProperties || storeProperties.id !== storeId) {
        return;
      }

      const storedCustomReturnReasons = normalizeCustomReturnReasons(
        storeProperties.resolvePortalOptions?.customReturnReasons,
      );
      const validReturnReasonIds = new Set<string>([
        ...RETURN_REASON_OPTIONS.map(({ key }) => key),
        ...storedCustomReturnReasons.map(({ id }) => id),
      ]);
      const storedReturnReasons = Array.isArray(
        storeProperties.resolvePortalOptions?.enabledReturnReasons,
      )
        ? storeProperties.resolvePortalOptions.enabledReturnReasons.filter(
            (reasonId: string) => validReturnReasonIds.has(reasonId),
          )
        : [];

      setCustomReturnReasons(storedCustomReturnReasons);
      setEnabledReturnReasons(
        storedReturnReasons.length > 0
          ? storedReturnReasons
          : [...DEFAULT_ENABLED_RETURN_REASONS],
      );

      const settings = storeProperties.policySettings;
      setInspectionSourceBaseline(getInspectionAuthority(settings));
      if (!settings) {
        setEligibleApprovalMode(ReturnApprovalMode.MANUAL_REVIEW);
        hydratedStoreIdRef.current = storeId;
        return;
      }

      const savedApproval = getRequestApprovalSettings(settings);
      setApprovalInvalid(savedApproval === null);
      if (savedApproval) setRequestApproval(savedApproval);

      setSettlementTimingRules(getSettlementTimingRules(settings));
      const inspectionAuthority = getInspectionAuthority(settings);
      setInspectionPolicy(defaultInspectionPolicy(inspectionAuthority.value));
      setInspectionBaseline(defaultInspectionPolicy(inspectionAuthority.value));
      setInspectionRepairRequested(false);
      setInspectionExplicitlyChosen(false);
      setInspectionAcknowledgement(null);
      if (settings.returnWindow) {
        setReturnWindow(settings.returnWindow.days || 30);
        setWindowStart(
          settings.returnWindow.trigger || PolicyTimeTrigger.DELIVERY_DATE,
        );
        setAllowUndelivered(settings.returnWindow.allowUndelivered ?? false);
      }
      if (settings.shipmentDeadline) {
        const shipmentDeadline = settings.shipmentDeadline;
        setShipmentDeadlineDays(
          shipmentDeadline.days ??
            DEFAULT_RETURN_WORKFLOW_SETTINGS.shipmentDeadline.days,
        );
        setShipmentDeadlineStartsAt(
          shipmentDeadline.startsAt === 'APPROVAL' ? 'APPROVAL' : 'LABEL_READY',
        );
        setShipmentReminderDaysInput(
          Array.isArray(shipmentDeadline.reminderDaysBeforeDeadline)
            ? shipmentDeadline.reminderDaysBeforeDeadline.join(', ')
            : DEFAULT_RETURN_WORKFLOW_SETTINGS.shipmentDeadline.reminderDaysBeforeDeadline.join(
                ', ',
              ),
        );
        setExpireReturnsAutomatically(
          shipmentDeadline.expireAutomatically ??
            DEFAULT_RETURN_WORKFLOW_SETTINGS.shipmentDeadline
              .expireAutomatically,
        );
      }
      if (settings.eligibility) {
        const eligibility = normalizeProductEligibilitySettings(
          settings.eligibility,
        );
        setProductRulesVersion(eligibility.productRulesVersion);
        setProductRulesEnabled(eligibility.isActive);
        setPersistedProductEligibilityFingerprint(
          getProductEligibilityFingerprint(eligibility),
        );
        setRequiresProductRulesReconfirmation(eligibility.hasLegacySavedRules);
        setProductRulesEnabledAt(eligibility.enabledAt);
        setFinalSaleEnforced(eligibility.finalSaleEnforced);
        setExcludedTags(eligibility.excludedTags);
        setExcludedCollections(eligibility.excludedCollections);
        setExcludedSkus(eligibility.excludedSkus);
        setDatedRules(eligibility.datedRules);
        setDatedRulesLoadError(eligibility.datedRulesError);
        setExcludedCollectionLabels(eligibility.excludedCollectionLabels);
      }
      const storedMode =
        settings.strictness?.mode || PolicyStrictnessMode.CUSTOMER_FIRST;
      setPolicyMode(storedMode);
      const rules = getApprovalRules(settings, storedMode);
      const legacyMaxReturns = settings.operationalControls?.abuseGuardrails?.maxReturnsPerMonth;
      const hydratedRules = settings.fraudRules?.maxPriorReturns === undefined && Number.isFinite(legacyMaxReturns)
        ? {...rules, maxPriorReturns: Number(legacyMaxReturns)} : rules;
      setApprovalRules(hydratedRules);
      setPriorReturnsBaseline(hydratedRules.maxPriorReturns);
      setPriorReturnsTouched(false);

      const preset = getPolicyModeApprovalPreset(storedMode);
      setEligibleApprovalMode(
        settings.strictness?.eligibleApprovalMode === ReturnApprovalMode.AUTO_DENY && !settings.approval ? ReturnApprovalMode.AUTO_DENY : savedApproval?.refund === 'AUTOMATIC' ? ReturnApprovalMode.AUTO_APPROVE : ReturnApprovalMode.MANUAL_REVIEW,
      );
      setFlaggedApprovalMode(
        settings.strictness?.flaggedApprovalMode || preset.flaggedApprovalMode,
      );

      const customerExperience =
        settings.operationalControls?.customerExperience;
      if (customerExperience) {
        const storedEvidenceRequirements =
          normalizeReturnEvidenceRequirements(customerExperience);
        setReturnEvidenceDefaults(storedEvidenceRequirements.defaults);
        setReturnEvidenceOverrides(storedEvidenceRequirements.byReason);
        setReturnInstructions(
          customerExperience.returnInstructions ||
            DEFAULT_CUSTOMER_RETURN_EXPERIENCE.returnInstructions,
        );
      }
      hydratedStoreIdRef.current = storeId;
    }, [storeId, storeProperties]);

    const presetReturnReasonMap = useMemo(() => {
      return new Map<string, PresetReturnReason>(
        RETURN_REASON_OPTIONS.map((reason) => [reason.key, reason]),
      );
    }, []);

    const visibleReturnReasons = useMemo(() => {
      const customReasonMap = new Map(
        customReturnReasons.map((reason) => [reason.id, reason]),
      );

      return enabledReturnReasons
        .map((reasonId) => {
          const presetReason = presetReturnReasonMap.get(reasonId);

          if (presetReason) {
            return {
              id: presetReason.key,
              title: presetReason.title,
              description: presetReason.description,
              analyticsKey: presetReason.key,
              isCustom: false,
            };
          }

          const customReason = customReasonMap.get(reasonId);

          if (!customReason) {
            return null;
          }

          return {
            id: customReason.id,
            title: customReason.label,
            description: 'Custom reason created by your team.',
            analyticsKey: customReason.analyticsKey,
            isCustom: true,
          };
        })
        .filter(Boolean) as Array<{
        id: string;
        title: string;
        description: string;
        analyticsKey: string;
        isCustom: boolean;
      }>;
    }, [customReturnReasons, enabledReturnReasons, presetReturnReasonMap]);

    const allReturnReasonIds = useMemo(
      () => [
        ...RETURN_REASON_OPTIONS.map(({ key }) => key),
        ...customReturnReasons.map(({ id }) => id),
      ],
      [customReturnReasons],
    );
    const returnReasonSummary = useMemo(
      () => getReturnReasonSummary(allReturnReasonIds, enabledReturnReasons),
      [allReturnReasonIds, enabledReturnReasons],
    );

    const togglePolicySection = (sectionId: string) => {
      setExpandedPolicySections((current) => ({
        ...current,
        [sectionId]: !current[sectionId],
      }));
    };

    const handlePolicyModeChange = (nextMode: PolicyStrictnessMode) => {
      if (!ownsApprovalScope() || activeSave.current || reviewRootUnverified) return;
      const preset = getPolicyModeApprovalPreset(nextMode);

      markUnsavedChanges();
      setPolicyMode(nextMode);
      setEligibleApprovalMode(preset.eligibleApprovalMode);
      setFlaggedApprovalMode(preset.flaggedApprovalMode);
      setApprovalRules(getPolicyModeRulePreset(nextMode));
      setRequestApproval(current => ({...current, refund: preset.eligibleApprovalMode === ReturnApprovalMode.AUTO_APPROVE ? 'AUTOMATIC' : 'MANUAL'}));
      if (storeProperties?.policySettings?.approval || approvalTouched) setApprovalTouched(true);
    };

    const changeRequestApproval = (kind: keyof RequestApprovalSettings, mode: RequestApprovalMode) => {
      if (!ownsApprovalScope() || activeSave.current || approvalInvalid || storeProperties?.id !== storeId || reviewRootUnverified) return;
      markUnsavedChanges();
      setApprovalTouched(true);
      setRequestApproval(current => ({...current, [kind]: mode}));
      if (kind === 'refund') setEligibleApprovalMode(mode === 'AUTOMATIC' ? ReturnApprovalMode.AUTO_APPROVE : ReturnApprovalMode.MANUAL_REVIEW);
    };

    const updateApprovalRule = <Key extends keyof ApprovalRules>(
      field: Key,
      value: ApprovalRules[Key],
    ) => {
      if (reviewRootUnverified) return;
      if (isApprovalSettings && field === 'maxPriorReturns') {
        if (returnHistoryLimitRestricted || isPolicySaveLoading || activeSave.current) return;
        setPriorReturnsTouched(true);
      }
      markUnsavedChanges();
      setApprovalRules((current) => ({ ...current, [field]: value }));
    };

    const toggleApprovalReviewReason = (reasonId: string) => {
      markUnsavedChanges();
      setApprovalRules((current) => ({
        ...current,
        reviewReasonIds: current.reviewReasonIds.includes(reasonId)
          ? current.reviewReasonIds.filter((id) => id !== reasonId)
          : [...current.reviewReasonIds, reasonId],
      }));
    };

    const handleFlaggedApprovalModeChange = (nextMode: ReturnApprovalMode) => {
      markUnsavedChanges();
      setFlaggedApprovalMode(nextMode);
    };

    const updateSettlementTimingRule = (
      settlementType: keyof SettlementTimingBranch,
      rule: SettlementTimingRule,
    ) => {
      markUnsavedChanges();
      setSettlementTimingRules((current) => ({
        ...current,
        [settlementType]: rule,
      }));
    };

    const timingBranch = (branch: 'inspectionRequired' | 'inspectionNotRequired'): SettlementTimingBranch => settlementTimingRules[branch] || {refund: settlementTimingRules.refund, storeCredit: settlementTimingRules.storeCredit};
    const updateTimingBranch = (branch: 'inspectionRequired' | 'inspectionNotRequired', settlementType: keyof SettlementTimingBranch, rule: SettlementTimingRule) => {
      markUnsavedChanges();
      setSettlementTimingRules(current => ({...current, [branch]: {...(current[branch] || {refund: current.refund, storeCredit: current.storeCredit}), [settlementType]: rule}}));
    };

    const setReturnReasonEnabled = (
      reasonKey: string,
      nextEnabled: boolean,
    ) => {
      markUnsavedChanges();
      setEnabledReturnReasons((current) => {
        const isEnabled = current.includes(reasonKey);

        if (isEnabled === nextEnabled) {
          return current;
        }

        return nextEnabled
          ? [...current, reasonKey]
          : current.filter((reason) => reason !== reasonKey);
      });
    };

    const updateDefaultEvidenceRequirement = (
      field: ReturnEvidenceField,
      value: ReturnEvidenceDefault,
    ) => {
      markUnsavedChanges();
      setReturnEvidenceDefaults((current) => ({
        ...current,
        [field]: value,
      }));
    };

    const updateReasonEvidenceRequirement = (
      reasonId: string,
      field: ReturnEvidenceField,
      value: ReturnEvidenceOverrideValue,
    ) => {
      markUnsavedChanges();
      setReturnEvidenceOverrides((current) =>
        updateReasonEvidenceOverride(current, reasonId, field, value),
      );
    };

    const handleAddCustomReturnReason = () => {
      const nextLabel = normalizeReturnReasonLabel(newCustomReturnReason);

      if (!nextLabel) {
        showNotification(
          NotificationTypes.error,
          'Enter a custom return reason before adding it',
        );
        return;
      }

      const existingLabels = [
        ...RETURN_REASON_OPTIONS.map(({ title }) => title.toLowerCase()),
        ...customReturnReasons.map(({ label }) => label.toLowerCase()),
      ];

      if (existingLabels.includes(nextLabel.toLowerCase())) {
        showNotification(
          NotificationTypes.error,
          'That return reason already exists',
        );
        return;
      }

      const analyticsKey = buildUniqueCustomAnalyticsKey(
        nextLabel,
        customReturnReasons.map(({ analyticsKey }) => analyticsKey),
      );
      const nextReason: CustomReturnReason = {
        id: `CUSTOM_${analyticsKey.toUpperCase()}_${Date.now().toString(36)}`,
        label: nextLabel,
        analyticsKey,
      };

      markUnsavedChanges();
      setCustomReturnReasons((current) => [...current, nextReason]);
      setEnabledReturnReasons((current) => [...current, nextReason.id]);
      setNewCustomReturnReason('');
    };

    const handleRemoveCustomReturnReason = (reasonId: string) => {
      markUnsavedChanges();
      setCustomReturnReasons((current) =>
        current.filter(({ id }) => id !== reasonId),
      );
      setEnabledReturnReasons((current) =>
        current.filter((reason) => reason !== reasonId),
      );
      setReturnEvidenceOverrides((current) => {
        const next = { ...current };
        delete next[reasonId];
        return next;
      });
    };

    const handleSave = async (): Promise<boolean> => {
      if (!ownsApprovalScope() || activeSave.current) return false;
      if (reviewRootUnverified) {setSaveStatus('error');showNotification(NotificationTypes.error, reviewRootVerificationMessage);return false;}
      if (workflowVerificationError) {showNotification(NotificationTypes.error, workflowVerificationError);return false;}
      if (returnHistorySaveBlocked) {setSaveStatus('error');showNotification(NotificationTypes.error, returnHistorySaveMessage);return false;}
      if (isApprovalSettings && approvalInvalid) {setSaveStatus('error');return false;}
      if (reviewCompatibilityIssue?.blocksSave) {setSaveStatus('error');showNotification(NotificationTypes.error, reviewCompatibilityIssue.message);return false;}
      if (approvalTouched && !storeProperties?.policySettings?.approval && eligibleApprovalMode === ReturnApprovalMode.AUTO_DENY) {
        showNotification(NotificationTypes.error, 'Choose a refund approval option before saving approval changes.');
        return false;
      }
      if (exchangeProcessing.dirty && exchangeProcessing.validation()) {await exchangeProcessing.save();return false;}
      if (!storeProperties || storeProperties.id !== storeId) {
        setSaveStatus('error');
        showNotification(
          NotificationTypes.error,
          'The selected store is still loading. Please try again in a moment.',
        );
        return false;
      }

      const validationError = (isApprovalSettings && writeInspection ? getInspectionPolicyError(inspectionPolicy) : null) || getPolicySettingsValidationError({
        page: settingsPage,
        returnWindow,
        shipmentDeadlineDays,
        shipmentReminderDaysInput,
        windowStart,
        approvalRules,
        eligibleApprovalMode,
        flaggedApprovalMode,
      });

      if (validationError) {
        showNotification(NotificationTypes.error, validationError);
        return false;
      }
      const shipmentReminderDays =
        parseShipmentReminderDays(shipmentReminderDaysInput) || [];

      if (isReturnsSettings && !visibleReturnReasons.length) {
        showNotification(
          NotificationTypes.error,
          'Please enable at least 1 return reason for the return portal',
        );
        return false;
      }

      const datedRulesError =
        datedRulesLoadError || getDatedProductRulesError(datedRules);
      if (isSharedPolicy && datedRulesError) {
        showNotification(NotificationTypes.error, datedRulesError);
        return false;
      }

      if (
        isSharedPolicy && productRulesEnabled &&
        !hasConfiguredProductEligibilityRule({
          finalSaleEnforced,
          excludedTags,
          excludedCollections,
          excludedSkus,
          datedRules,
        })
      ) {
        showNotification(
          NotificationTypes.error,
          'Choose at least one product exclusion before saving.',
        );
        return false;
      }

      if (
        isSharedPolicy &&
        productRulesEnabled &&
        (productEligibilityActivationState?.storeId !== storeId ||
          !productEligibilityActivationState.ready)
      ) {
        showNotification(
          NotificationTypes.error,
          productEligibilityActivationState?.storeId === storeId &&
            productEligibilityActivationState.reason
            ? productEligibilityActivationState.reason
            : 'Wait for the Shopify catalog check to finish or sync the catalog before saving active enforcement.',
        );
        return false;
      }

      const currentSettings = (storeProperties?.policySettings ||
        {}) as PolicySettingsShape;
      const storedEligibility = currentSettings.eligibility || {};
      const eligibility = {
        ...storedEligibility,
        finalSaleEnforced,
        excludedTags,
        excludedCollections,
        excludedSkus,
        ...(datedRules.length || storedEligibility.datedRules !== undefined
          ? { datedRules: normalizeDatedProductRules(datedRules) }
          : {}),
        excludedCollectionLabels,
        productRulesEnabled,
        ...(productRulesVersion !== undefined ? { productRulesVersion } : {}),
        enabledAt: productRulesEnabled ? productRulesEnabledAt || null : null,
      };
      const currentReturnWindow = currentSettings.returnWindow;
      const currentRefundTiming =
        currentSettings.refundTiming ||
        DEFAULT_RETURN_WORKFLOW_SETTINGS.refundTiming;
      const currentFraudRules =
        currentSettings.fraudRules ||
        DEFAULT_RETURN_WORKFLOW_SETTINGS.fraudRules;
      const currentReasonRules =
        currentSettings.reasonRules ||
        DEFAULT_RETURN_WORKFLOW_SETTINGS.reasonRules;
      const enabledPresetReasons = visibleReturnReasons
        .filter(({ isCustom }) => !isCustom)
        .map(({ id }) => id);
      const enabledCustomReasonIds = visibleReturnReasons
        .filter(({ isCustom }) => isCustom)
        .map(({ id }) => id);

      const returnWorkflowInput = {
        storeId,
        returnWindow: {
          days: returnWindow,
          trigger: windowStart,
          deliveryBufferDays: currentReturnWindow?.deliveryBufferDays ?? 0,
          holidayExtension: currentReturnWindow?.holidayExtension ?? false,
          allowUndelivered,
        },
        shipmentDeadline: {
          days: shipmentDeadlineDays,
          startsAt: shipmentDeadlineStartsAt,
          reminderDaysBeforeDeadline: shipmentReminderDays,
          expireAutomatically: expireReturnsAutomatically,
        },
        refundTiming: {
          trigger: settlementTimingRules.refund.trigger,
          delayDays: settlementTimingRules.refund.delayDays,
          appliesTo: Array.isArray(currentRefundTiming.appliesTo)
            ? currentRefundTiming.appliesTo
            : DEFAULT_RETURN_WORKFLOW_SETTINGS.refundTiming.appliesTo,
          skipAutomationWhenFlagged:
            currentRefundTiming.skipAutomationWhenFlagged ??
            DEFAULT_RETURN_WORKFLOW_SETTINGS.refundTiming
              .skipAutomationWhenFlagged,
        },
        fraudRules: {
          maxItemsInReturn: approvalRules.maxItemsInReturn,
          maxReturnValue: approvalRules.maxReturnValue,
          maxOrderValue: approvalRules.maxReturnValue,
          maxPriorReturns: approvalRules.maxPriorReturns,
          maxPriorClaims: currentFraudRules.maxPriorClaims,
          requireEvidenceForAutoApproval:
            approvalRules.requireEvidenceForAutoApproval,
          reviewUnprotectedOrders: approvalRules.reviewUnprotectedOrders,
        },
        reasonRules: {
          damagedRequiresReview:
            approvalRules.reviewReasonIds.includes('DAMAGED'),
          damagedRequiresPhotos:
            currentReasonRules.damagedRequiresPhotos ??
            DEFAULT_RETURN_WORKFLOW_SETTINGS.reasonRules.damagedRequiresPhotos,
          damagedNotifiesMerchant:
            currentReasonRules.damagedNotifiesMerchant ??
            DEFAULT_RETURN_WORKFLOW_SETTINGS.reasonRules
              .damagedNotifiesMerchant,
          reviewReasonIds: approvalRules.reviewReasonIds,
        },
      };

      setSaveStatus('idle');
      setPartialSaveMessage(null);
      let savedEligibilityInput: ProductEligibilitySettingsInput = eligibility;
      const hadUnsavedCustomerRestrictions =
        isSharedPolicy && customerRestrictions.dirty;
      let customerRestrictionsSaved = false;
      let processingSavedBeforeWorkflow = false;
      let workflowAcknowledgementUnverified = false;
      const saveWorkflow = hasUnsavedChanges || customerRestrictions.dirty || !exchangeProcessing.dirty;
      const saveOperation = {};activeSave.current = saveOperation;
      try {
        setPolicySavePending(true);
        if (isSharedPolicy && !(await customerRestrictions.save())) {
          setSaveStatus('error');
          showNotification(
            NotificationTypes.error,
            'Customer restrictions could not be saved. Try again.',
          );
          return false;
        }
        customerRestrictionsSaved = hadUnsavedCustomerRestrictions;
        if (!ownsApprovalScope()) return false;
        // Turning inspection off or enabling processing from Off requires the workflow first.
        // Other inspected targets first disable processing or select manual release safely.
        const processingFirst = isApprovalSettings && inspectionPolicy.enabled && exchangeProcessing.source?.enabled !== false && exchangeProcessing.dirty;
        if (processingFirst) {
          if (!(await exchangeProcessing.save())) {if (ownsApprovalScope()) setSaveStatus('error');return false;}
          if (!ownsApprovalScope()) return false;
          processingSavedBeforeWorkflow = true;
        }
        const result = saveWorkflow ? await saveReturnWorkflowSettings({
          variables: {
            input: getPolicyPageSaveInput(settingsPage, {
              ...returnWorkflowInput,
              ...(approvalTouched ? {approval: requestApproval} : {}),
              policySettings: {
                eligibility,
                strictness: {
                  mode: policyMode,
                  eligibleApprovalMode,
                  flaggedApprovalMode,
                },
                settlementTiming: {
                  refund: settlementTimingRules.refund,
                  storeCredit: settlementTimingRules.storeCredit,
                  ...(settlementTimingRules.inspectionRequired ? {inspectionRequired: settlementTimingRules.inspectionRequired} : {}),
                  ...(settlementTimingRules.inspectionNotRequired ? {inspectionNotRequired: settlementTimingRules.inspectionNotRequired} : {}),
                },
                operationalControls: {
                  ...(!isApprovalSettings || writeInspection ? {inspection: inspectionPolicy} : {}),
                  customerExperience: {
                    requireCustomerNotes:
                      returnEvidenceDefaults.notes === 'REQUIRED',
                    requireReturnPhotos:
                      returnEvidenceDefaults.photos === 'REQUIRED',
                    evidenceRequirements: {
                      defaults: returnEvidenceDefaults,
                      byReason: returnEvidenceOverrides,
                    },
                    returnInstructions: returnInstructions.trim(),
                  },
                  ...(!isApprovalSettings ? {abuseGuardrails: {
                    maxReturnsPerMonth: approvalRules.maxPriorReturns,
                    flagHighVelocity: approvalRules.maxPriorReturns > 0,
                  }} : reviewAbusePatch ? {abuseGuardrails: reviewAbusePatch} : {}),
                },
                returnReasons: {
                  enabledPresetReasons,
                  enabledCustomReasonIds,
                  customReasons: customReturnReasons,
                },
              },
              resolvePortalOptions: {
                enabledReturnReasons: visibleReturnReasons.map(({ id }) => id),
                customReturnReasons,
              },
            }),
          },
        }) : null;
        if (!ownsApprovalScope()) return false;
        if (isApprovalSettings && saveWorkflow) {
          const acknowledgement = result?.data?.saveReturnWorkflowSettings;
          const acknowledgedAuthority = getInspectionAuthority(acknowledgement?.policySettings);
          const expectedAuthority = writeInspection ? getInspectionAuthority({operationalControls: {inspection: inspectionPolicy}}) : inspectionAuthority;
          if (acknowledgement?.storeId !== storeId || !sameInspectionAuthority(acknowledgedAuthority, expectedAuthority) ||
            (reviewAbusePatch && !isEqual(acknowledgement?.policySettings?.operationalControls?.abuseGuardrails, expectedWrittenAbuse))) {
            workflowAcknowledgementUnverified = true;
            setWorkflowVerificationError(`${processingSavedBeforeWorkflow ? 'Exchange processing timing saved. ' : ''}Review settings save could not be verified. Reload Review settings before trying again.`);
            throw new Error('Review settings acknowledgement could not be verified');
          }
          setInspectionAcknowledgement({scope: approvalScope, beforePolicy: savedPolicy, authority: acknowledgedAuthority, policySettings: acknowledgement.policySettings});
          setPriorReturnsBaseline(approvalRules.maxPriorReturns);
          setPriorReturnsTouched(false);
          if (writeInspection) {
            setInspectionSourceBaseline(acknowledgedAuthority);
            setInspectionBaseline(inspectionPolicy);
            setInspectionRepairRequested(false);
            setInspectionExplicitlyChosen(false);
          }
        }
        if (saveWorkflow && approvalTouched) {
          const serverSettings = result?.data?.saveReturnWorkflowSettings?.policySettings;
          const serverApproval = serverSettings && Object.prototype.hasOwnProperty.call(serverSettings, 'approval') ? getRequestApprovalSettings(serverSettings) : null;
          if (!serverApproval || serverApproval.refund !== requestApproval.refund || serverApproval.exchange !== requestApproval.exchange) throw new Error('Approval settings acknowledgement could not be verified');
          setRequestApproval(serverApproval);
          setApprovalTouched(false);
        }
        const serverEligibility =
          result?.data?.saveReturnWorkflowSettings?.policySettings?.eligibility;
        if (
          serverEligibility &&
          typeof serverEligibility === 'object' &&
          !Array.isArray(serverEligibility)
        ) {
          savedEligibilityInput = serverEligibility;
        }
        if (exchangeProcessing.dirty && !processingSavedBeforeWorkflow) {
          if (saveWorkflow) setHasUnsavedChanges(false);
          if (!(await exchangeProcessing.save())) {
            if (!ownsApprovalScope()) return false;
            setSaveStatus(saveWorkflow ? 'partial' : 'error');
            return false;
          }
          if (!ownsApprovalScope()) return false;
        }
      } catch (error) {
        if (!ownsApprovalScope()) return false;
        if (processingSavedBeforeWorkflow) setPartialSaveMessage(workflowAcknowledgementUnverified
          ? 'Exchange processing timing saved. Review settings save could not be verified. Reload Review settings before trying again.'
          : 'Exchange processing timing saved. Approval, inspection and refund settings could not be saved. Try again.');
        setSaveStatus(customerRestrictionsSaved || processingSavedBeforeWorkflow ? 'partial' : 'error');
        showNotification(
          NotificationTypes.error,
          customerRestrictionsSaved
            ? 'Customer restrictions saved. Other policy settings could not be saved; try again.'
            : 'Policy settings were not saved',
        );
        apiErrorHandler(error as ApolloError);
        return false;
      } finally {
        if (activeSave.current === saveOperation) activeSave.current = null;
        if (ownsApprovalScope()) setPolicySavePending(false);
      }

      if (!ownsApprovalScope()) return false;

      const savedEligibility = normalizeProductEligibilitySettings(
        savedEligibilityInput,
      );
      setProductRulesVersion(savedEligibility.productRulesVersion);
      setProductRulesEnabled(savedEligibility.isActive);
      setProductRulesEnabledAt(savedEligibility.enabledAt);
      setFinalSaleEnforced(savedEligibility.finalSaleEnforced);
      setExcludedTags(savedEligibility.excludedTags);
      setExcludedCollections(savedEligibility.excludedCollections);
      setExcludedSkus(savedEligibility.excludedSkus);
      setDatedRules(savedEligibility.datedRules);
      setDatedRulesLoadError(savedEligibility.datedRulesError);
      setExcludedCollectionLabels(savedEligibility.excludedCollectionLabels);
      setPersistedProductEligibilityFingerprint(
        getProductEligibilityFingerprint(savedEligibility),
      );
      setRequiresProductRulesReconfirmation(
        savedEligibility.hasLegacySavedRules,
      );
      setSaveStatus('saved');
      setHasUnsavedChanges(false);
      showNotification(NotificationTypes.success, 'Policy saved.');

      if (refetchStoreProperties) {
        try {
          await refetchStoreProperties();
        } catch (error) {
          if (!ownsApprovalScope()) return false;
          if (isApprovalSettings) setWorkflowVerificationError('Review settings saved, but the latest settings could not be refreshed. Reload Review settings before making further changes.');
          showNotification(
            NotificationTypes.error,
            'Policy saved, but the latest settings could not be refreshed.',
          );
          apiErrorHandler(error as ApolloError);
        }
      }

      return ownsApprovalScope();
    };

    const windowDescription = `${returnWindow} day${
      returnWindow === 1 ? '' : 's'
    } from ${WINDOW_TRIGGER_LABELS[windowStart]}`;
    const flaggedApprovalOption = getApprovalModeOption(flaggedApprovalMode);
    const approvalModeCustomized =
      isPolicyApprovalPresetCustomized(
        policyMode,
        eligibleApprovalMode,
        flaggedApprovalMode,
      ) || isPolicyRulePresetCustomized(policyMode, approvalRules);
    const isPolicySaveLoading =
      exchangeProcessing.saving ||
      policySavePending ||
      customerRestrictions.saving ||
      saveReturnWorkflowSettingsLoading;
    const pageSettingsLoading =
      (!storeId || storeProperties?.id !== storeId || hydratedStoreIdRef.current !== storeId);
    const savedPolicy = storeProperties?.policySettings as any;
    const rawInspectionAuthority = getInspectionAuthority(savedPolicy);
    const retainedAcknowledgement = inspectionAcknowledgement?.scope === approvalScope && isEqual(inspectionAcknowledgement.beforePolicy, savedPolicy) ? inspectionAcknowledgement : null;
    const inspectionAuthority = retainedAcknowledgement ? retainedAcknowledgement.authority : rawInspectionAuthority;
    const acknowledgedPolicy = retainedAcknowledgement ? retainedAcknowledgement.policySettings : savedPolicy;
    const savedInspection = inspectionAuthority.value;
    const savedInspectionTrusted = inspectionAuthorityVerified(inspectionAuthority);
    const inspectionSourceChanged = !sameInspectionAuthority(inspectionAuthority, inspectionSourceBaseline);
    const processingVerified = !exchangeProcessing.loading && !exchangeProcessing.unavailable && !exchangeProcessing.error && !!exchangeProcessing.source && !!exchangeProcessing.draft;
    const inspectionChanged = !isEqual(inspectionBaseline, inspectionPolicy);
    const writeInspection = isApprovalSettings && (inspectionChanged || inspectionRepairRequested || (inspectionExplicitlyChosen && !savedInspectionTrusted));
    const preserveControlsParent = !writeInspection && (inspectionAuthority.key === 'operational_controls' || !inspectionAuthority.wrapperKnown);
    const returnHistoryLimitRestricted = isApprovalSettings && preserveControlsParent;
    const returnHistoryLimitChanged = priorReturnsTouched || approvalRules.maxPriorReturns !== priorReturnsBaseline;
    const returnHistorySaveBlocked = returnHistoryLimitRestricted && returnHistoryLimitChanged;
    const returnHistorySaveMessage = 'The return-history limit cannot be saved until you confirm the inspection setup. Confirm the setup above or reload Review settings to keep the saved limit.';
    const object = (value: any) => value !== null && typeof value === 'object' && !Array.isArray(value);
    const reviewRootUnverified = isApprovalSettings && !pageSettingsLoading && !object(savedPolicy);
    const reviewRootVerificationMessage = 'Workflow settings could not be verified. Reload Review settings before changing approval, inspection or processing.';
    const controlsForGuardrails = [acknowledgedPolicy?.operationalControls, acknowledgedPolicy?.operational_controls].find(object);
    const savedAbuseGuardrails = [controlsForGuardrails?.abuseGuardrails, controlsForGuardrails?.abuse_guardrails].find(object);
    const reviewAbusePatch = !preserveControlsParent && returnHistoryLimitChanged
      ? {maxReturnsPerMonth: approvalRules.maxPriorReturns, flagHighVelocity: approvalRules.maxPriorReturns > 0}
      : writeInspection ? savedAbuseGuardrails : undefined;
    const existingCanonicalAbuse = acknowledgedPolicy?.operationalControls?.abuseGuardrails;
    const expectedWrittenAbuse = reviewAbusePatch ? {...(object(existingCanonicalAbuse) ? existingCanonicalAbuse : {}), ...reviewAbusePatch} : undefined;
    const approvalRulesSummary = [
      approvalRules.maxReturnValue > 0 ? `Up to $${approvalRules.maxReturnValue}` : 'Any value',
      approvalRules.maxItemsInReturn > 0 ? `${approvalRules.maxItemsInReturn} item${approvalRules.maxItemsInReturn === 1 ? '' : 's'}` : 'Any quantity',
      !returnHistoryLimitChanged && savedAbuseGuardrails ? 'Existing return-history checks retained' : approvalRules.maxPriorReturns > 0
        ? `Review after ${approvalRules.maxPriorReturns} prior return${approvalRules.maxPriorReturns === 1 ? '' : 's'}` : 'No history limit',
    ].join(' · ');
    const reviewCompatibilityIssue = isApprovalSettings ? getReviewExchangeCompatibility({
      inspection: inspectionPolicy, inspectionChanged: writeInspection,
      inspectionSourceChanged,
      processing: exchangeProcessing.draft, processingChanged: exchangeProcessing.dirty,
      processingVerified,
      inspectionVerified: writeInspection ? completeInspectionPolicy(inspectionPolicy) : savedInspectionTrusted,
      allReturnsInspectionVerified: writeInspection ? hasCanonicalAllReturnsInspection(inspectionPolicy) : hasCanonicalAllReturnsInspection(savedInspection),
    }) : null;
    const changeInspectionPolicy = (value: typeof inspectionPolicy) => {
      if (isPolicySaveLoading || reviewRootUnverified) return;
      const next = savedInspectionTrusted ? value : copyKnownInspectionShape(value, defaultInspectionPolicy());
      markUnsavedChanges();
      setInspectionPolicy(next);
      setInspectionExplicitlyChosen(true);
      setInspectionRepairRequested(false);
      if (next.mode !== 'NEVER' && next.mode !== inspectionPolicy.mode) {
        setSettlementTimingRules(current => ({...current, inspectionRequired: current.inspectionRequired || {refund: {trigger: 'INSPECTION_APPROVED', delayDays: 0}, storeCredit: {trigger: 'INSPECTION_APPROVED', delayDays: 0}}, ...(next.mode === 'MATCHING' ? {inspectionNotRequired: current.inspectionNotRequired || {refund: current.refund, storeCredit: current.storeCredit}} : {})}));
      }
    };
    const useInspectionManualRelease = () => {
      if (!processingVerified || isPolicySaveLoading || !ownsApprovalScope() || reviewRootUnverified) return;
      const complete = copyKnownInspectionShape(
        defaultInspectionPolicy({...inspectionPolicy, enabled: true, mode: 'ALWAYS'}),
        defaultInspectionPolicy({enabled: true, mode: 'ALWAYS'}),
      );
      if (!hasCanonicalAllReturnsInspection(complete)) {
        showNotification(NotificationTypes.error, 'Review the inspection rule values before using All returns inspection and manual release.');
        return;
      }
      changeInspectionPolicy(complete);
      setInspectionRepairRequested(true);
      exchangeProcessing.change({enabled: true, trigger: 'MANUAL_APPROVAL'});
    };
    const confirmCurrentInspection = () => {
      if (!returnHistoryLimitRestricted || !savedInspectionTrusted || inspectionSourceChanged || isPolicySaveLoading || !ownsApprovalScope()) return;
      const complete = copyKnownInspectionShape(inspectionPolicy, defaultInspectionPolicy());
      if (!completeInspectionPolicy(complete)) {
        showNotification(NotificationTypes.error, 'Review the inspection rule values before confirming this setup.');
        return;
      }
      changeInspectionPolicy(complete);
      setInspectionRepairRequested(true);
    };
    const inspectedExchangesReady = !pageSettingsLoading && !isPolicySaveLoading && !inspectionSourceChanged &&
      hasCanonicalAllReturnsInspection(savedInspection) && isEqual(savedInspection, inspectionPolicy) &&
      !exchangeProcessing.loading && !exchangeProcessing.error && !exchangeProcessing.dirty &&
      exchangeProcessing.source?.enabled === true && exchangeProcessing.source.trigger === 'MANUAL_APPROVAL' &&
      exchangeProcessing.draft?.enabled === true && exchangeProcessing.draft.trigger === 'MANUAL_APPROVAL';
    const currentProductEligibilityFingerprint =
      getProductEligibilityFingerprint({
        productRulesVersion,
        productRulesEnabled,
        enabledAt: productRulesEnabledAt,
        finalSaleEnforced,
        excludedTags,
        excludedCollections,
        excludedSkus,
        datedRules,
        excludedCollectionLabels,
      });
    const productRulesActivationPending =
      currentProductEligibilityFingerprint !==
      persistedProductEligibilityFingerprint;

    const generatedPolicyText = useMemo(() => {
      const lines = [
        'Return and Exchange Policy',
        '',
        `Customers can request a return or exchange for eligible items within ${returnWindow} day${
          returnWindow === 1 ? '' : 's'
        } of the ${WINDOW_TRIGGER_LABELS[windowStart]}.`,
      ];

      if (
        productRulesEnabled &&
        (finalSaleEnforced ||
          excludedCollections.length > 0 ||
          excludedTags.length > 0 ||
          excludedSkus.length > 0 ||
          datedRules.some((rule) => rule.enabled))
      ) {
        lines.push('');
        lines.push('Product exclusions');

        if (finalSaleEnforced) {
          lines.push('Final sale items are not eligible for return or exchange.');
        }
        if (excludedCollections.length > 0) {
          lines.push(
            'Items in the following collections are not eligible for return or exchange:',
          );
          lines.push(
            excludedCollections
              .map((id) => excludedCollectionLabels?.[id] || id)
              .join(', '),
          );
        }
        if (excludedTags.length > 0) {
          lines.push(
            'Items with the following tags are not eligible for return or exchange:',
          );
          lines.push(excludedTags.join(', '));
        }
        if (excludedSkus.length > 0) {
          lines.push(
            'Items with the following SKUs are not eligible for return or exchange:',
          );
          lines.push(excludedSkus.join(', '));
        }
        datedRules.filter((rule) => rule.enabled).forEach((rule) => {
          lines.push(
            `${rule.name}: ${describeDatedProductRule(rule)} Matching items are not eligible for return or exchange.`,
          );
        });
      }

      lines.push('');
      lines.push(`If an item must be sent back, send it within ${shipmentDeadlineDays} days of ${
        shipmentDeadlineStartsAt === 'APPROVAL' ? 'approval' : 'the return label becoming ready'
      }.`);
      lines.push('');
      lines.push('Available resolutions depend on the store’s return and exchange settings and item eligibility.');
      lines.push('Contact the store with questions before sending an item back.');

      return lines.join('\n');
    }, [
      excludedCollectionLabels,
      excludedCollections,
      excludedSkus,
      excludedTags,
      datedRules,
      finalSaleEnforced,
      productRulesEnabled,
      shipmentDeadlineDays,
      shipmentDeadlineStartsAt,
      returnWindow,
      windowStart,
    ]);

    const policyPreviewText = generatedPolicyText;

    const policySummaryHeadline = `Returns and exchanges use the same ${windowDescription} window, product exclusions and customer restrictions.`;
    const activeProductRuleCount = productRulesEnabled
      ? Number(finalSaleEnforced) +
        excludedCollections.length +
        excludedTags.length +
        excludedSkus.length +
        datedRules.filter((rule) => rule.enabled).length
      : 0;

    const hasCustomerRestrictions =
      customerRestrictions.value.enabled &&
      customerRestrictions.value.customerTags.length +
        customerRestrictions.value.blockedEmails.length +
        customerRestrictions.value.blockedCustomers.length >
        0;

    const policySummaryCards = useMemo(() => {
      return [
        {
          label: 'Eligible requests',
          value: windowDescription,
          description: `Checked against the ${windowDescription} window.`,
        },
        {
          label: 'Send-back deadline',
          value: `${shipmentDeadlineDays} days from ${shipmentDeadlineStartsAt === 'APPROVAL' ? 'approval' : 'label ready'}`,
          description: 'Applies when the original item must be sent back.',
        },
        {
          label: 'Product exclusions',
          value: productRulesActivationPending
            ? `${activeProductRuleCount} product rule${
                activeProductRuleCount === 1 ? '' : 's'
              } pending save`
            : productRulesEnabled
            ? `${activeProductRuleCount} active product rule${
                activeProductRuleCount === 1 ? '' : 's'
              }`
            : `Standard ${windowDescription} window`,
          description: productRulesActivationPending
            ? 'Save the policy to apply this product exclusion change.'
            : productRulesEnabled
            ? hasCustomerRestrictions
              ? 'Return window, product exclusions and customer restrictions.'
              : 'Return window plus product exclusions.'
            : hasCustomerRestrictions
            ? 'Return window and customer restrictions.'
            : 'Return window only.',
        },
      ];
    }, [
      activeProductRuleCount,
      hasCustomerRestrictions,
      productRulesActivationPending,
      productRulesEnabled,
      shipmentDeadlineDays,
      shipmentDeadlineStartsAt,
      windowDescription,
    ]);

    const customerExperienceSummary = useMemo(() => {
      const overrideCount = Object.keys(returnEvidenceOverrides).length;

      return `Note ${
        RETURN_EVIDENCE_DEFAULT_SUMMARY[returnEvidenceDefaults.notes]
      }; photo ${
        RETURN_EVIDENCE_DEFAULT_SUMMARY[returnEvidenceDefaults.photos]
      }; ${overrideCount} reason override${overrideCount === 1 ? '' : 's'}`;
    }, [returnEvidenceDefaults, returnEvidenceOverrides]);

    const isAiPolishComingSoon = true;

    const handleCopyGeneratedPolicy = async () => {
      try {
        await navigator.clipboard.writeText(policyPreviewText);
        showNotification(NotificationTypes.success, 'Policy copied.');
      } catch (error) {
        showNotification(
          NotificationTypes.error,
          'Unable to copy the generated policy text',
        );
      }
    };

    const saveAllSettings = async () => {
      if (!ownsApprovalScope() || activeSave.current) return false;
      if (reviewRootUnverified) {setSaveStatus('error');showNotification(NotificationTypes.error, reviewRootVerificationMessage);return false;}
      const capturedDefectSave = defectPolicySave.current;
      if (workflowVerificationError) {showNotification(NotificationTypes.error, workflowVerificationError);return false;}
      if (returnHistorySaveBlocked) {setSaveStatus('error');showNotification(NotificationTypes.error, returnHistorySaveMessage);return false;}
      if (reviewCompatibilityIssue?.blocksSave) {setSaveStatus('error');showNotification(NotificationTypes.error, reviewCompatibilityIssue.message);return false;}
      if ((!defectPolicyDirty || hasUnsavedChanges || customerRestrictions.dirty || exchangeProcessing.dirty) && !(await handleSave())) return false;
      if (!ownsApprovalScope()) return false;
      return defectPolicyDirty && capturedDefectSave ? (await capturedDefectSave()) && ownsApprovalScope() : true;
    };

    return (
      <SettingsViewWrapper
        sectionId="returns-settings"
        activeNav={`resolution-portal/returns/${
          isApprovalSettings ? 'approval' : isReturnsSettings ? 'settings' : 'policy'
        }`}
      >
        <UnsavedChangesGuard
          hasUnsavedChanges={hasUnsavedChanges || customerRestrictions.dirty || defectPolicyDirty || exchangeProcessing.dirty}
          onSave={saveAllSettings}
        />
        <div className="w-full h-full bg-[#f6f6f7]">
          <div className="max-w-6xl mx-auto px-6 py-6 space-y-6">
            <ReturnsSettingsTabs
              activeTab={isApprovalSettings ? 'approval' : isReturnsSettings ? 'returns' : 'policy'}
            />

            {/* Policy Settings Content */}
            <div className="sticky top-0 z-20 -mx-2 flex flex-col gap-4 border-b border-gray-200 bg-[#f6f6f7]/95 px-2 py-3 backdrop-blur-sm md:flex-row md:items-start md:justify-between">
              <div>
                {!isReturnsSettings && <SharedResolutionSettingsNotice />}
                <h1 className="text-2xl font-bold text-gray-900 tracking-tight">
                  {isApprovalSettings ? 'Review' : isReturnsSettings ? 'Returns' : 'Policy'}
                </h1>
                <p className="text-gray-500 mt-1 max-w-2xl text-sm">
                  {isApprovalSettings
                    ? 'Set request approval, item inspection and processing timing in one workflow.'
                    : isReturnsSettings
                    ? 'Choose return reasons and the details customers provide.'
                    : 'Set shared eligibility, deadlines and customer restrictions.'}
                </p>
              </div>
              <div className="flex items-center gap-2">
                {(hasUnsavedChanges || customerRestrictions.dirty || defectPolicyDirty || exchangeProcessing.dirty) && (
                  <span
                    role="status"
                    className="text-xs font-semibold text-amber-700"
                  >
                    Unsaved changes
                  </span>
                )}
                {saveStatus === 'saved' && (
                  <span
                    role="status"
                    className="text-xs font-semibold text-green-700"
                  >
                    Saved
                  </span>
                )}
                {saveStatus === 'error' && (
                  <span
                    role="status"
                    className="text-xs font-semibold text-red-700"
                  >
                    {workflowVerificationError ? 'Save unverified' : 'Not saved'}
                  </span>
                )}
                {saveStatus === 'partial' && (
                  <span
                    role="status"
                    className="text-xs font-semibold text-amber-700"
                  >
                    Partially saved
                  </span>
                )}
                <Button
                  size={SizeEnum.sm}
                  onClick={saveAllSettings}
                  isLoading={isPolicySaveLoading || defectPolicySaving}
                  disabled={pageSettingsLoading || !!reviewCompatibilityIssue?.blocksSave || !!workflowVerificationError || returnHistorySaveBlocked || reviewRootUnverified}
                >
                  {isPolicySaveLoading || defectPolicySaving ? 'Saving...' : 'Save'}
                </Button>
              </div>
            </div>

            {isReturnsSettings && <ReturnsProgramStatus />}

            {(workflowVerificationError || reviewRootUnverified) && <div role="alert" className="space-y-3 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
              <p>{reviewRootUnverified ? reviewRootVerificationMessage : workflowVerificationError}</p>
              <Button variant="outlined" onClick={() => window.location.reload()}>Reload Review settings</Button>
            </div>}
            {returnHistorySaveBlocked && !reviewRootUnverified && <p role="alert" className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">{returnHistorySaveMessage}</p>}
            {saveStatus === 'partial' && !workflowVerificationError && (
              <p
                role="alert"
                className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800"
              >
                {partialSaveMessage || (exchangeProcessing.error ? 'Approval settings saved. Exchange processing timing could not be saved. Reload exchange processing settings and try again.' : 'Customer restrictions saved. Other policy settings could not be saved; try again.')}
              </p>
            )}

            {pageSettingsLoading ? (
              <p role="status" className="rounded-xl border border-gray-200 bg-white p-5 text-sm text-gray-600">{isApprovalSettings ? 'Loading approval and timing settings…' : isReturnsSettings ? 'Loading return settings…' : 'Loading policy settings…'}</p>
            ) : (
            <div
              className="grid grid-cols-1 gap-4 2xl:grid-cols-12"
              onChangeCapture={(event) => {
                if (
                  (event.target as HTMLElement).id !==
                  'new-custom-return-reason' &&
                  (event.target as HTMLElement).id !== 'blocked-customer-tag' &&
                  (event.target as HTMLElement).id !== 'exchange-processing-trigger'
                ) {
                  markUnsavedChanges();
                }
              }}
            >
              {/* Main Column */}
              <div
                className={classNames(
                  'space-y-4',
                  isSharedPolicy ? '2xl:col-span-8' : '2xl:col-span-12',
                )}
              >
                <SettingsSectionGroup visible={isApprovalSettings}>
                  <ReviewProcessingWorkflow
                    approval={<>
                    <p className="mt-1 text-sm text-gray-500">Set the approval mode for each resolution. Flagged requests and reasons requiring review still need a team decision.</p>
                    {approvalInvalid && <p role="alert" className="mt-3 rounded-lg bg-red-50 p-3 text-sm text-red-700">Saved approval settings could not be verified. Reload before editing approvals.</p>}
                    {!storeProperties?.policySettings?.approval && eligibleApprovalMode === ReturnApprovalMode.AUTO_DENY && <p className="mt-3 rounded-lg bg-amber-50 p-3 text-sm text-amber-800">Existing refund policy automatically denies eligible requests. Choose an approval option to replace that legacy setting.</p>}
                    {!storeProperties?.policySettings?.approval && <p className="mt-3 text-xs leading-5 text-gray-500">Refunds for cheaper exchanges currently require review under legacy policies. Select an approval option, including the current selection, then Save to apply these choices to new requests.</p>}
                    <div className="mt-4 grid gap-4 md:grid-cols-2">
                      {(['refund', 'exchange'] as const).map(kind => <fieldset key={kind} disabled={isPolicySaveLoading || approvalInvalid} className="rounded-lg border border-gray-200 p-4">
                        <legend className="px-1 text-sm font-semibold text-gray-900">{kind === 'refund' ? 'Refund requests' : 'Exchange requests'}</legend>
                        <p className="mb-3 text-xs leading-5 text-gray-500">{kind === 'refund' ? 'Store-credit requests and refunds for cheaper exchanges use this approval choice too. Automatic outcomes wait for required checks and their processing timing.' : 'Replacements still wait for the configured release event and any required additional payment.'}</p>
                        <div className="space-y-3">
                          {(['MANUAL', 'AUTOMATIC'] as const).map(mode => <label key={mode} className="flex cursor-pointer items-start gap-2 text-sm text-gray-800">
                            <input type="radio" name={`${kind}-request-approval`} value={mode} checked={!approvalInvalid && !(kind === 'refund' && !storeProperties?.policySettings?.approval && eligibleApprovalMode === ReturnApprovalMode.AUTO_DENY) && requestApproval[kind] === mode} onChange={() => changeRequestApproval(kind, mode)} onClick={() => {
                              if (!storeProperties?.policySettings?.approval && requestApproval[kind] === mode && !(kind === 'refund' && eligibleApprovalMode === ReturnApprovalMode.AUTO_DENY)) changeRequestApproval(kind, mode);
                            }} className="mt-0.5" />
                            <span>{mode === 'MANUAL' ? 'Review each request' : 'Automatically approve eligible requests'}</span>
                          </label>)}
                        </div>
                      </fieldset>)}
                    </div>
                    <p className="mt-4 border-t border-gray-100 pt-3 text-xs leading-5 text-gray-500">Approval does not issue a refund or release a replacement. Refund timing, exchange release conditions and any additional payment still apply.</p>
                    <p className="mt-3 rounded-lg border border-blue-100 bg-blue-50 px-3 py-2 text-xs leading-5 text-blue-800">Higher-priced replacements stay on hold until Shopify confirms the additional payment. Cheaper replacements follow the refund approval and timing rules.</p>
                    </>}
                    inspection={<>
                    <InspectionPolicyControls storeId={storeId} value={inspectionPolicy} unverified={!savedInspectionTrusted && !inspectionExplicitlyChosen} reasons={visibleReturnReasons.map(reason => ({id: reason.id, label: reason.title}))} onChange={changeInspectionPolicy} />
                    {returnHistoryLimitRestricted && savedInspectionTrusted && <div className="mt-3 space-y-2 text-xs text-gray-600"><p>Confirm the current inspection setup to edit the return-history limit below.</p><Button variant="outlined" disabled={isPolicySaveLoading || inspectionSourceChanged} onClick={confirmCurrentInspection}>Confirm current inspection setup</Button></div>}
                      {inspectionEnabled && <p role="status" className={classNames('mt-3 rounded-lg px-3 py-2 text-xs', inspectedExchangesReady ? 'bg-blue-50 text-blue-800' : 'bg-amber-50 text-amber-800')}>
                        {inspectedExchangesReady
                          ? 'Customers can request exchanges with All returns inspection and manual release. Fully approve the returned items, then separately release the exchange. Request approval remains a separate decision.'
                          : 'To offer inspected exchanges, save inspection as All returns and enable Manual exchange release after inspection. Matching returns and automatic processing events do not support customer exchanges yet. You can turn inspection off to use other processing events. Eligible returns remain available.'}
                      </p>}
                    </>}
                    replacement={<>
                    <p className="text-sm font-medium text-gray-800">Approve the request first, then process the replacement when its return event is met.</p>
                    <div className="mt-3 flex flex-wrap items-center justify-between gap-3"><p className="text-xs text-gray-500">Choose the return event required before a replacement can be processed.</p><Toggle ariaLabel="Enable exchange processing" rightLabel="Enable exchange processing" isEnabled={exchangeProcessing.draft?.enabled || false} disabled={isPolicySaveLoading || exchangeProcessing.unavailable || !!exchangeProcessing.error?.includes('Reload')} onChange={enabled => exchangeProcessing.change({enabled, trigger: null})} /></div>
                    {exchangeProcessing.loading && <p role="status" className="text-sm text-gray-500">Loading exchange processing settings…</p>}
                    {!exchangeProcessing.loading && exchangeProcessing.unavailable && <p role="status" className="text-sm text-amber-800">Exchange processing settings are unavailable. Existing processing rules are preserved.</p>}
                    {exchangeProcessing.draft && <label htmlFor="exchange-processing-trigger" className="block max-w-lg text-sm font-medium text-gray-800">Process replacements after<select id="exchange-processing-trigger" value={exchangeProcessing.draft.trigger || ''} disabled={!exchangeProcessing.draft.enabled || isPolicySaveLoading || !!exchangeProcessing.error?.includes('Reload')} onChange={event => exchangeProcessing.change({enabled: true, trigger: event.target.value as ExchangeProcessingTrigger || null})} className="mt-1 block w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm"><option value="">Choose a required event</option>{EXCHANGE_PROCESSING_TRIGGERS.filter(item => item.value !== 'IN_TRANSIT' || exchangeProcessing.source?.trigger === 'IN_TRANSIT').map(item => <option key={item.value} value={item.value}>{item.label}</option>)}</select></label>}
                    {exchangeProcessing.draft?.trigger === 'MANUAL_APPROVAL' && <p className="rounded-lg bg-blue-50 p-3 text-xs leading-5 text-blue-800">A team member must release the replacement separately from approving the exchange request. Complete and approve any required inspection before releasing it.</p>}
                    {exchangeProcessing.draft && !exchangeProcessing.draft.enabled && <p className="rounded-lg bg-amber-50 p-3 text-xs leading-5 text-amber-800">Exchange processing is off. Customer exchange eligibility is configured separately; enable processing to let newly confirmed exchanges progress.</p>}
                    {exchangeProcessing.error && <div role="alert" className="space-y-2 rounded-lg bg-red-50 p-3 text-sm text-red-700"><p>{exchangeProcessing.error}</p><Button variant="outlined" disabled={isPolicySaveLoading} onClick={() => exchangeProcessing.reload()}>Reload exchange processing settings</Button></div>}

                    </>}
                    compatibility={<>
                    {reviewCompatibilityIssue && !reviewRootUnverified && <div role={reviewCompatibilityIssue.blocksSave ? 'alert' : 'status'} className="mt-4 space-y-3 rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
                      <p>{reviewCompatibilityIssue.message}</p>
                      {!reviewCompatibilityIssue.blocksSave && <p className="text-xs">Existing settings are preserved. Unrelated review changes can still be saved.</p>}
                      {reviewCompatibilityIssue.kind === 'source' ? <Button variant="outlined" disabled={isPolicySaveLoading} onClick={() => window.location.reload()}>Reload Review settings</Button> : reviewCompatibilityIssue.kind === 'unverified' ? !exchangeProcessing.error && <Button variant="outlined" disabled={exchangeProcessing.loading || isPolicySaveLoading} onClick={() => exchangeProcessing.reload()}>Reload exchange processing settings</Button> : <Button variant="outlined" disabled={isPolicySaveLoading || !processingVerified} onClick={useInspectionManualRelease}>Use All returns inspection and manual release</Button>}
                    </div>}
                    </>}
                    settlement={<>
                      {inspectionEnabled && !settlementTimingRules.inspectionRequired && <button type="button" className="mt-4 rounded-lg border border-gray-300 px-3 py-2 text-sm" onClick={() => {markUnsavedChanges(); setSettlementTimingRules(current => ({...current, inspectionRequired: {refund: {trigger: 'INSPECTION_APPROVED', delayDays: 0}, storeCredit: {trigger: 'INSPECTION_APPROVED', delayDays: 0}}}));}}>Set inspection settlement timing</button>}
                      {!inspectionEnabled || !settlementTimingRules.inspectionRequired ? <div className="mt-4 space-y-3">
                        <SettlementTimingCard
                          id="refund-timing"
                          outcome="refund"
                          title="Original payment refund"
                          description="Control when money is sent back to the payment method."
                          rule={settlementTimingRules.refund}
                          onChange={(refund) =>
                            updateSettlementTimingRule('refund', refund)
                          }
                        />
                        <SettlementTimingCard
                          id="store-credit-timing"
                          outcome="storeCredit"
                          title="Store credit"
                          description="Control when credit is added to the customer's store account."
                          rule={settlementTimingRules.storeCredit}
                          onChange={(storeCredit) =>
                            updateSettlementTimingRule(
                              'storeCredit',
                              storeCredit,
                            )
                          }
                        />
                      </div> : (
                        <div className="mt-5 space-y-5">
                          {(['inspectionRequired', ...(inspectionPolicy.mode === 'MATCHING' ? ['inspectionNotRequired'] : [])] as ('inspectionRequired' | 'inspectionNotRequired')[]).map(branch => (
                            <fieldset key={branch} className="rounded-xl border border-gray-200 bg-white p-4 sm:p-5">
                              <legend className="flex items-center gap-2 px-1 text-base font-semibold text-gray-900">
                                <span aria-hidden="true" className={`h-2 w-2 rounded-full ${branch === 'inspectionRequired' ? 'bg-blue-600' : 'bg-gray-400'}`} />
                                {branch === 'inspectionRequired' ? 'Inspection required' : 'No inspection required'}
                              </legend>
                              {branch === 'inspectionRequired' && <p className="mb-3 text-xs text-gray-500">Funds stay on hold until received items pass inspection.</p>}
                              <div className="space-y-3">
                                <SettlementTimingCard
                                  id={`${branch}-refund`}
                                  outcome="refund"
                                  title="Refund"
                                  description="Original payment method"
                                  accessibleLabel="Refund"
                                  inspectionRequired={branch === 'inspectionRequired'}
                                  rule={timingBranch(branch).refund}
                                  onChange={rule => updateTimingBranch(branch, 'refund', rule)}
                                />
                                <SettlementTimingCard
                                  id={`${branch}-credit`}
                                  outcome="storeCredit"
                                  title="Store credit"
                                  description="Customer store account"
                                  accessibleLabel="Store credit"
                                  inspectionRequired={branch === 'inspectionRequired'}
                                  rule={timingBranch(branch).storeCredit}
                                  onChange={rule => updateTimingBranch(branch, 'storeCredit', rule)}
                                />
                              </div>
                            </fieldset>
                          ))}
                        </div>
                      )}
                      <p className="mt-4 text-xs text-gray-500">Keep-item resolutions settle after approval. Other review holds still apply.</p>                    </>}
                    summary={reviewRootUnverified ? reviewRootVerificationMessage : reviewCompatibilityIssue && ['source', 'canonical', 'inspection'].includes(reviewCompatibilityIssue.kind) ? reviewCompatibilityIssue.message : getReplacementWorkflowSummary(inspectionPolicy, exchangeProcessing.draft, processingVerified, requestApproval.exchange)}
                    pendingChanges={hasUnsavedChanges || exchangeProcessing.dirty}
                    disabled={isPolicySaveLoading || defectPolicySaving || reviewRootUnverified}
                  />
                  {/* Policy Mode */}
                  <CollapsiblePolicySection
                    id="approval-rules"
                    title="Review rules"
                    description="Choose a profile, then adjust what is approved or reviewed."
                    summary={approvalRulesSummary}
                    icon={<ShieldCheckIcon className="h-5 w-5" />}
                    badge={
                      <span className="inline-flex rounded-full border border-blue-200 bg-blue-50 px-3 py-1 text-xs font-medium text-blue-700">
                        {POLICY_MODE_LABELS[policyMode]}
                        {approvalModeCustomized ? ' · Customized' : ''}
                      </span>
                    }
                    isExpanded={expandedPolicySections.policyMode}
                    onToggle={() => togglePolicySection('policyMode')}
                  >
                    <fieldset disabled={reviewRootUnverified} className="m-0 min-w-0 border-0 p-0">
                    <div className="p-4">
                      <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
                        {/* Strict */}
                        <button
                          type="button"
                          onClick={() =>
                            handlePolicyModeChange(PolicyStrictnessMode.STRICT)
                          }
                          disabled={isApprovalSettings && isPolicySaveLoading}
                          className={classNames(
                            'relative flex h-full flex-col items-start rounded-lg border p-4 text-left transition-all',
                            policyMode === PolicyStrictnessMode.STRICT
                              ? 'border-blue-500 bg-blue-50 ring-1 ring-blue-500'
                              : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50',
                          )}
                        >
                          <div className="flex w-full items-center justify-between mb-2">
                            <span className="font-semibold text-gray-900">
                              Strict
                            </span>
                            {policyMode === PolicyStrictnessMode.STRICT && (
                              <div className="h-2 w-2 rounded-full bg-blue-600" />
                            )}
                          </div>
                          <p className="text-xs text-gray-500 leading-relaxed">
                            {
                              getPolicyModeBehavior(PolicyStrictnessMode.STRICT)
                                .cardDescription
                            }
                          </p>
                          <p className="mt-3 text-[11px] font-medium leading-4 text-gray-600">
                            {getPolicyModePresetSummary(
                              PolicyStrictnessMode.STRICT,
                            )}
                          </p>
                        </button>

                        {/* Semi-Automatic */}
                        <button
                          type="button"
                          onClick={() =>
                            handlePolicyModeChange(
                              PolicyStrictnessMode.SEMI_AUTOMATIC,
                            )
                          }
                          disabled={isApprovalSettings && isPolicySaveLoading}
                          className={classNames(
                            'relative flex h-full flex-col items-start rounded-lg border p-4 text-left transition-all',
                            policyMode === PolicyStrictnessMode.SEMI_AUTOMATIC
                              ? 'border-blue-500 bg-blue-50 ring-1 ring-blue-500'
                              : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50',
                          )}
                        >
                          <div className="flex w-full items-center justify-between mb-2">
                            <span className="font-semibold text-gray-900">
                              Semi-automatic
                            </span>
                            {policyMode ===
                              PolicyStrictnessMode.SEMI_AUTOMATIC && (
                              <div className="h-2 w-2 rounded-full bg-blue-600" />
                            )}
                          </div>
                          <p className="text-xs text-gray-500 leading-relaxed">
                            {
                              getPolicyModeBehavior(
                                PolicyStrictnessMode.SEMI_AUTOMATIC,
                              ).cardDescription
                            }
                          </p>
                          <p className="mt-3 text-[11px] font-medium leading-4 text-gray-600">
                            {getPolicyModePresetSummary(
                              PolicyStrictnessMode.SEMI_AUTOMATIC,
                            )}
                          </p>
                        </button>

                        {/* Customer-First */}
                        <button
                          type="button"
                          onClick={() =>
                            handlePolicyModeChange(
                              PolicyStrictnessMode.CUSTOMER_FIRST,
                            )
                          }
                          disabled={isApprovalSettings && isPolicySaveLoading}
                          className={classNames(
                            'relative flex h-full flex-col items-start rounded-lg border p-4 text-left transition-all',
                            policyMode === PolicyStrictnessMode.CUSTOMER_FIRST
                              ? 'border-blue-500 bg-blue-50 ring-1 ring-blue-500'
                              : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50',
                          )}
                        >
                          <div className="flex w-full items-center justify-between mb-2">
                            <span className="font-semibold text-gray-900">
                              Customer-first
                            </span>
                            {policyMode ===
                              PolicyStrictnessMode.CUSTOMER_FIRST && (
                              <div className="h-2 w-2 rounded-full bg-blue-600" />
                            )}
                          </div>
                          <p className="text-xs text-gray-500 leading-relaxed">
                            {
                              getPolicyModeBehavior(
                                PolicyStrictnessMode.CUSTOMER_FIRST,
                              ).cardDescription
                            }
                          </p>
                          <p className="mt-3 text-[11px] font-medium leading-4 text-gray-600">
                            {getPolicyModePresetSummary(
                              PolicyStrictnessMode.CUSTOMER_FIRST,
                            )}
                          </p>
                        </button>
                      </div>

                      <div className="mt-5 border-t border-gray-100 pt-5">
                        <div className="flex flex-wrap items-center justify-between gap-2">
                          <div>
                            <h4 className="text-sm font-semibold text-gray-900">
                              Outcomes
                            </h4>
                            <p className="mt-1 text-xs text-gray-500">
                              Choose what happens when checks pass or flag a
                              request.
                            </p>
                          </div>
                          <span
                            className={classNames(
                              'rounded-full px-2.5 py-1 text-xs font-medium',
                              approvalModeCustomized
                                ? 'border border-amber-200 bg-amber-50 text-amber-700'
                                : 'bg-gray-100 text-gray-600',
                            )}
                          >
                            {approvalModeCustomized ? 'Customized' : 'Preset'}
                          </span>
                        </div>

                        <div className="mt-4 grid grid-cols-1 gap-3 md:grid-cols-2">
                          <div
                            className="rounded-lg border border-gray-200 bg-gray-50 p-4"
                          >
                            <span className="block text-sm font-semibold text-gray-900">
                              Eligible refund requests
                            </span>
                            <span className="mt-1 block text-xs text-gray-500">
                              The request is within policy and has no risk
                              flags.
                            </span>
                            <p className="mt-3 text-sm font-medium text-gray-900">{requestApproval.refund === 'AUTOMATIC' ? 'Automatically approve eligible requests' : 'Review each request'}</p>
                            <span className="mt-2 block text-xs text-gray-500">
                              Set this in Refund requests above. Choosing a review profile also updates the refund approval mode.
                            </span>
                          </div>

                          <label
                            htmlFor="flagged-approval-mode"
                            className="rounded-lg border border-gray-200 bg-gray-50 p-4"
                          >
                            <span className="block text-sm font-semibold text-gray-900">
                              When a request is flagged
                            </span>
                            <span className="mt-1 block text-xs text-gray-500">
                              An exclusion, return limit, or risk check was
                              triggered.
                            </span>
                            <select
                              id="flagged-approval-mode"
                              value={flaggedApprovalMode}
                              onChange={(event) =>
                                handleFlaggedApprovalModeChange(
                                  event.target.value as ReturnApprovalMode,
                                )
                              }
                              className="mt-3 block w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-900 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                            >
                              {APPROVAL_MODE_OPTIONS.filter(
                                ({ value }) =>
                                  value !== ReturnApprovalMode.AUTO_APPROVE,
                              ).map(({ value, label }) => (
                                <option key={value} value={value}>
                                  {label}
                                </option>
                              ))}
                            </select>
                            <span className="mt-2 block text-xs text-gray-500">
                              {flaggedApprovalOption.description}
                            </span>
                          </label>
                        </div>

                        <div className="mt-5 border-t border-gray-100 pt-5">
                          <h4 className="text-sm font-semibold text-gray-900">
                            Review thresholds
                          </h4>
                          <p className="mt-1 text-xs text-gray-500">
                            Requests above a limit use the flagged outcome.
                            Enter 0 to turn a limit off.
                          </p>
                          <div className="mt-4 grid grid-cols-1 gap-3 md:grid-cols-3">
                            <label
                              htmlFor="approval-max-return-value"
                              className="rounded-lg border border-gray-200 bg-gray-50 p-4"
                            >
                              <span className="block text-sm font-semibold text-gray-900">
                                Return value
                              </span>
                              <div className="relative mt-3">
                                <span className="pointer-events-none absolute inset-y-0 left-3 flex items-center text-sm text-gray-500">
                                  $
                                </span>
                                <NonNegativeNumberInput
                                  id="approval-max-return-value"
                                  min={0}
                                  value={approvalRules.maxReturnValue}
                                  onChange={(event) =>
                                    updateApprovalRule(
                                      'maxReturnValue',
                                      Math.max(
                                        0,
                                        Number(event.target.value) || 0,
                                      ),
                                    )
                                  }
                                  className="block w-full rounded-lg border border-gray-300 bg-white py-2 pl-7 pr-3 text-sm text-gray-900 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                                />
                              </div>
                            </label>

                            <label
                              htmlFor="approval-max-items"
                              className="rounded-lg border border-gray-200 bg-gray-50 p-4"
                            >
                              <span className="block text-sm font-semibold text-gray-900">
                                Item quantity
                              </span>
                              <NonNegativeNumberInput
                                id="approval-max-items"
                                min={0}
                                step={1}
                                value={approvalRules.maxItemsInReturn}
                                onChange={(event) =>
                                  updateApprovalRule(
                                    'maxItemsInReturn',
                                    Math.max(
                                      0,
                                      Number(event.target.value) || 0,
                                    ),
                                  )
                                }
                                className="mt-3 block w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                              />
                            </label>

                            <label
                              htmlFor="approval-max-prior-returns"
                              className="rounded-lg border border-gray-200 bg-gray-50 p-4"
                            >
                              <span className="block text-sm font-semibold text-gray-900">
                                Customer return history
                              </span>
                              <NonNegativeNumberInput
                                id="approval-max-prior-returns"
                                min={0}
                                step={1}
                                value={approvalRules.maxPriorReturns}
                                disabled={returnHistoryLimitRestricted || isPolicySaveLoading}
                                onChange={(event) =>
                                  updateApprovalRule(
                                    'maxPriorReturns',
                                    Math.max(
                                      0,
                                      Number(event.target.value) || 0,
                                    ),
                                  )
                                }
                                className="mt-3 block w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                              />
                              <span className="mt-2 block text-xs text-gray-500">
                                Prior returns before review
                              </span>
                              {returnHistoryLimitRestricted && !reviewRootUnverified && <span className="mt-2 block text-xs text-amber-800">Confirm your inspection setup above before changing the return-history limit. Other review settings can still be saved.</span>}
                              {!returnHistoryLimitChanged && savedAbuseGuardrails && <span className="mt-2 block text-xs text-gray-500">Existing return-history checks are kept until you change this limit.</span>}
                            </label>
                          </div>
                        </div>

                        <div className="mt-5 grid grid-cols-1 gap-4 border-t border-gray-100 pt-5 lg:grid-cols-2">
                          <section className="rounded-lg border border-gray-200 p-4">
                            <h4 className="text-sm font-semibold text-gray-900">
                              Reasons requiring review
                            </h4>
                            <div className="mt-3 grid grid-cols-1 gap-2 sm:grid-cols-2">
                              {visibleReturnReasons.map((reason) => (
                                <label
                                  key={reason.id}
                                  className="flex cursor-pointer items-center gap-2 rounded-md px-2 py-1.5 hover:bg-gray-50"
                                >
                                  <input
                                    type="checkbox"
                                    checked={approvalRules.reviewReasonIds.includes(
                                      reason.id,
                                    )}
                                    onChange={() =>
                                      toggleApprovalReviewReason(reason.id)
                                    }
                                    className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                                  />
                                  <span className="text-sm text-gray-700">
                                    {reason.title}
                                  </span>
                                </label>
                              ))}
                            </div>
                          </section>

                          <section className="rounded-lg border border-gray-200 p-4">
                            <h4 className="text-sm font-semibold text-gray-900">
                              Evidence and safeguards
                            </h4>
                            <div className="mt-3 divide-y divide-gray-100">
                              <div className="flex items-center justify-between gap-4 py-3 first:pt-0">
                                <span className="text-sm text-gray-700">
                                  Review requests with no note or photo
                                </span>
                                <Toggle
                                  isEnabled={
                                    approvalRules.requireEvidenceForAutoApproval
                                  }
                                  onChange={(isEnabled) =>
                                    updateApprovalRule(
                                      'requireEvidenceForAutoApproval',
                                      isEnabled,
                                    )
                                  }
                                />
                              </div>
                              <div className="flex items-center justify-between gap-4 py-3">
                                <span className="text-sm text-gray-700">
                                  Review orders without Delivery Guarantee
                                </span>
                                <Toggle
                                  isEnabled={
                                    approvalRules.reviewUnprotectedOrders
                                  }
                                  onChange={(isEnabled) =>
                                    updateApprovalRule(
                                      'reviewUnprotectedOrders',
                                      isEnabled,
                                    )
                                  }
                                />
                              </div>
                            </div>
                          </section>
                        </div>
                      </div>
                    </div>
                    </fieldset>
                  </CollapsiblePolicySection>


                  <DefectResolutionPolicy onSaveStateChange={({ dirty, saving, save }) => {
                    setDefectPolicyDirty(dirty); setDefectPolicySaving(saving); defectPolicySave.current = save;
                  }} />
                </SettingsSectionGroup>

                <SettingsSectionGroup visible={isSharedPolicy}>
                  <CustomerRestrictionsPolicy
                    key={storeId}
                    {...customerRestrictions}
                    saving={isPolicySaveLoading}
                    onChange={(value) => {
                      markUnsavedChanges();
                      customerRestrictions.onChange(value);
                    }}
                  />
                  {/* Return Window */}
                  <CollapsiblePolicySection
                    id="return-window"
                    title="Return window"
                    description="Set how long customers have to request a return or exchange."
                    summary={windowDescription}
                    icon={<CalendarIcon className="h-5 w-5" />}
                    badge={
                      <span className="rounded-full bg-gray-100 px-3 py-1 text-xs font-medium text-gray-600">
                        {returnWindow} days
                      </span>
                    }
                    isExpanded={expandedPolicySections.returnWindow}
                    onToggle={() => togglePolicySection('returnWindow')}
                  >
                    <div className="p-5 space-y-6">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div>
                          <label
                            className="block text-sm font-medium text-gray-700 mb-1.5"
                            htmlFor="return-window"
                          >
                            Return window
                          </label>
                          <div className="relative">
                            <NonNegativeNumberInput
                              id="return-window"
                              min={1}
                              step={1}
                              value={returnWindow}
                              onChange={(e) =>
                                handleReturnWindowChange(Number(e.target.value))
                              }
                              className="block w-full pl-4 pr-12 py-2.5 text-sm border border-gray-300 rounded-lg focus:ring-black focus:border-black"
                            />
                            <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                              <span className="text-gray-500 text-xs font-medium uppercase">
                                Days
                              </span>
                            </div>
                          </div>
                        </div>
                        <div>
                          <label
                            className="block text-sm font-medium text-gray-700 mb-1.5"
                            htmlFor="window-start"
                          >
                            Starts from
                          </label>
                          <select
                            id="window-start"
                            value={windowStart}
                            onChange={(e) =>
                              handleWindowStartChange(
                                e.target.value as PolicyTimeTrigger,
                              )
                            }
                            className="block w-full pl-3 pr-10 py-2.5 text-sm border border-gray-300 rounded-lg focus:ring-black focus:border-black"
                          >
                            <option value={PolicyTimeTrigger.DELIVERY_DATE}>
                              Delivery date (recommended)
                            </option>
                            <option value={PolicyTimeTrigger.ORDER_DATE}>
                              Order date
                            </option>
                            <option value={PolicyTimeTrigger.FULFILLMENT_DATE}>
                              Fulfillment date
                            </option>
                          </select>
                        </div>
                      </div>
                      {windowStart === PolicyTimeTrigger.DELIVERY_DATE && (
                        <div className="pt-4 border-t border-gray-100 mt-6">
                          <Toggle
                            isEnabled={allowUndelivered}
                            onChange={(isEnabled) => {
                              markUnsavedChanges();
                              setAllowUndelivered(isEnabled);
                            }}
                            rightLabel="Allow requests before delivery"
                            rightDescription="If delivery is not confirmed, use the order date."
                          />
                        </div>
                      )}
                    </div>
                  </CollapsiblePolicySection>

                  <CollapsiblePolicySection
                    id="shipment-deadline"
                    title="Ship-back deadline"
                    description="Set how long approved customers have to hand the return to a carrier."
                    summary={`${shipmentDeadlineDays} days from ${
                      shipmentDeadlineStartsAt === 'APPROVAL'
                        ? 'approval'
                        : 'label readiness'
                    }`}
                    icon={<CalendarIcon className="h-5 w-5" />}
                    badge={
                      <span className="rounded-full bg-gray-100 px-3 py-1 text-xs font-medium text-gray-600">
                        {shipmentDeadlineDays} days
                      </span>
                    }
                    isExpanded={expandedPolicySections.shipmentDeadline}
                    onToggle={() => togglePolicySection('shipmentDeadline')}
                  >
                    <div className="space-y-5 p-5">
                      <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
                        <label htmlFor="shipment-deadline-days">
                          <span className="block text-sm font-medium text-gray-700">
                            Days to ship
                          </span>
                          <NonNegativeNumberInput
                            id="shipment-deadline-days"
                            min={1}
                            step={1}
                            value={shipmentDeadlineDays}
                            onChange={(event) => {
                              markUnsavedChanges();
                              setShipmentDeadlineDays(
                                Number(event.target.value),
                              );
                            }}
                            className="mt-1.5 block w-full rounded-lg border border-gray-300 px-3 py-2.5 text-sm focus:border-black focus:ring-black"
                          />
                        </label>
                        <label htmlFor="shipment-deadline-start">
                          <span className="block text-sm font-medium text-gray-700">
                            Start counting
                          </span>
                          <select
                            id="shipment-deadline-start"
                            value={shipmentDeadlineStartsAt}
                            onChange={(event) => {
                              markUnsavedChanges();
                              setShipmentDeadlineStartsAt(
                                event.target.value as ShipmentDeadlineStartsAt,
                              );
                            }}
                            className="mt-1.5 block w-full rounded-lg border border-gray-300 px-3 py-2.5 text-sm focus:border-black focus:ring-black"
                          >
                            <option value="LABEL_READY">
                              When the label or QR code is ready
                            </option>
                            <option value="APPROVAL">
                              When the return is approved
                            </option>
                          </select>
                        </label>
                      </div>

                      <label htmlFor="shipment-reminder-days">
                        <span className="block text-sm font-medium text-gray-700">
                          Reminder days before deadline
                        </span>
                        <input
                          id="shipment-reminder-days"
                          type="text"
                          value={shipmentReminderDaysInput}
                          onChange={(event) => {
                            markUnsavedChanges();
                            setShipmentReminderDaysInput(event.target.value);
                          }}
                          placeholder="10, 2, 0"
                          className="mt-1.5 block w-full rounded-lg border border-gray-300 px-3 py-2.5 text-sm focus:border-black focus:ring-black"
                        />
                        <span className="mt-1.5 block text-xs leading-5 text-gray-500">
                          Use comma-separated whole numbers. Use 0 for a
                          reminder on the deadline.
                        </span>
                      </label>

                      <div className="border-t border-gray-100 pt-4">
                        <Toggle
                          isEnabled={expireReturnsAutomatically}
                          onChange={(isEnabled) => {
                            markUnsavedChanges();
                            setExpireReturnsAutomatically(isEnabled);
                          }}
                          rightLabel="Expire overdue returns automatically"
                          rightDescription="Close approved returns when the ship-back deadline passes."
                        />
                      </div>
                    </div>
                  </CollapsiblePolicySection>

                  {/* Eligibility & Exclusions */}
                  <CollapsiblePolicySection
                    id="eligibility-exclusions"
                    title="Product exclusions"
                    description="Exclude matching products for all purchases or a specific purchase period."
                    summary={
                      productRulesActivationPending
                        ? 'Pending save'
                        : productRulesEnabled
                        ? 'Active'
                        : 'Not active'
                    }
                    icon={<BanIcon className="h-5 w-5" />}
                    badge={
                      <span
                        className={classNames(
                          'rounded-full px-3 py-1 text-xs font-medium',
                          productRulesActivationPending
                            ? 'bg-amber-100 text-amber-800'
                            : productRulesEnabled
                            ? 'bg-green-100 text-green-800'
                            : 'bg-gray-100 text-gray-600',
                        )}
                      >
                        {productRulesActivationPending
                          ? 'Pending save'
                          : productRulesEnabled
                          ? 'Active'
                          : 'Review before activation'}
                      </span>
                    }
                    isExpanded={expandedPolicySections.exclusions}
                    onToggle={() => togglePolicySection('exclusions')}
                  >
                    <ProductEligibilityControls
                      storeId={storeId}
                      productRulesEnabled={productRulesEnabled}
                      activationPending={productRulesActivationPending}
                      requiresReconfirmation={
                        requiresProductRulesReconfirmation
                      }
                      finalSaleEnforced={finalSaleEnforced}
                      excludedTags={excludedTags}
                      excludedCollections={excludedCollections}
                      excludedSkus={excludedSkus}
                      datedRules={datedRules}
                      datedRulesLoadError={datedRulesLoadError}
                      onDatedRulesChange={handleDatedRulesChange}
                      disabled={isPolicySaveLoading}
                      excludedCollectionLabels={excludedCollectionLabels}
                      onProductRulesEnabledChange={
                        handleProductRulesEnabledChange
                      }
                      onFinalSaleEnforcedChange={handleFinalSaleEnforcedChange}
                      onExclusionsChange={handleProductExclusionsChange}
                      onActivationStateChange={
                        setProductEligibilityActivationState
                      }
                    />
                  </CollapsiblePolicySection>

                </SettingsSectionGroup>
                <SettingsSectionGroup visible={isReturnsSettings}>
                  <CollapsiblePolicySection
                    id="return-reasons"
                    title="Return reasons"
                    description="Choose customer-facing return reasons."
                    summary={`${returnReasonSummary.enabledLabel} · ${returnReasonSummary.disabledLabel}`}
                    icon={<SupportIcon className="h-5 w-5" />}
                    badge={
                      <span className="inline-flex items-center rounded-full border border-blue-200 bg-blue-50 px-3 py-1 text-xs font-medium text-blue-700">
                        {returnReasonSummary.enabledLabel}
                      </span>
                    }
                    isExpanded={expandedPolicySections.returnReasons}
                    onToggle={() => togglePolicySection('returnReasons')}
                  >
                    <div className="p-5 space-y-5">
                      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                        <div className="rounded-lg border border-green-200 bg-green-50 px-4 py-3">
                          <p className="text-[11px] font-semibold uppercase tracking-[0.08em] text-green-700">
                            Enabled reasons
                          </p>
                          <p className="mt-1 text-sm font-semibold text-green-950">
                            {returnReasonSummary.enabledLabel}
                          </p>
                          <p className="mt-1 text-xs text-green-800">
                            Shown to customers.
                          </p>
                        </div>
                        <div className="rounded-lg border border-gray-200 bg-gray-50 px-4 py-3">
                          <p className="text-[11px] font-semibold uppercase tracking-[0.08em] text-gray-500">
                            Disabled reasons
                          </p>
                          <p className="mt-1 text-sm font-semibold text-gray-900">
                            {returnReasonSummary.disabledLabel}
                          </p>
                          <p className="mt-1 text-xs text-gray-500">
                            Hidden from customers.
                          </p>
                        </div>
                      </div>

                      <div className="space-y-4">
                        {RETURN_REASON_GROUPS.map((group) => {
                          const groupEnabledCount = group.keys.filter((key) =>
                            enabledReturnReasons.includes(key),
                          ).length;

                          return (
                            <div
                              key={group.title}
                              className="rounded-lg border border-gray-200 bg-white"
                            >
                              <div className="flex items-center justify-between gap-3 border-b border-gray-100 px-4 py-3">
                                <div>
                                  <h4 className="text-sm font-semibold text-gray-900">
                                    {group.title}
                                  </h4>
                                  <p className="mt-0.5 text-xs text-gray-500">
                                    {group.description}
                                  </p>
                                </div>
                                <span className="shrink-0 rounded-full bg-gray-100 px-2.5 py-1 text-xs font-medium text-gray-600">
                                  {groupEnabledCount}/{group.keys.length}{' '}
                                  enabled
                                </span>
                              </div>
                              <div className="divide-y divide-gray-100">
                                {group.keys.map((reasonKey) => {
                                  const reason =
                                    presetReturnReasonMap.get(reasonKey);

                                  if (!reason) {
                                    return null;
                                  }

                                  const isEnabled =
                                    enabledReturnReasons.includes(reason.key);

                                  return (
                                    <div
                                      key={reason.key}
                                      className="flex w-full items-center justify-between gap-4 px-4 py-3"
                                    >
                                      <div className="min-w-0">
                                        <p
                                          className={classNames(
                                            'text-sm font-medium',
                                            isEnabled
                                              ? 'text-gray-950'
                                              : 'text-gray-500',
                                          )}
                                        >
                                          {reason.title}
                                        </p>
                                        <p className="mt-0.5 truncate text-xs text-gray-500">
                                          {reason.description}
                                        </p>
                                      </div>
                                      <Toggle
                                        isEnabled={isEnabled}
                                        onChange={(nextEnabled) =>
                                          setReturnReasonEnabled(
                                            reason.key,
                                            nextEnabled,
                                          )
                                        }
                                        isSmall
                                        ariaLabel={`${reason.title} return reason`}
                                      />
                                    </div>
                                  );
                                })}
                              </div>
                            </div>
                          );
                        })}
                      </div>

                      <div className="rounded-lg border border-dashed border-blue-200 bg-blue-50/30 p-4">
                        <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
                          <div>
                            <h4 className="text-sm font-semibold text-gray-900">
                              Custom reasons
                            </h4>
                            <p className="mt-1 text-xs text-gray-500">
                              Add store-specific reasons. New reasons start
                              enabled.
                            </p>
                          </div>
                          <span className="inline-flex self-start rounded-full border border-blue-200 bg-white px-3 py-1 text-xs font-medium text-blue-700">
                            {customReturnReasons.length} custom
                          </span>
                        </div>

                        <div className="mt-4 flex flex-col gap-3 md:flex-row">
                          <input
                            id="new-custom-return-reason"
                            type="text"
                            value={newCustomReturnReason}
                            onChange={(event) =>
                              setNewCustomReturnReason(event.target.value)
                            }
                            onKeyDown={(event) => {
                              if (event.key === 'Enter') {
                                event.preventDefault();
                                handleAddCustomReturnReason();
                              }
                            }}
                            maxLength={60}
                            placeholder='Add a custom reason, like "Ordered multiple sizes"'
                            className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 shadow-sm transition-colors focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-100"
                          />
                          <Button
                            size={SizeEnum.sm}
                            onClick={handleAddCustomReturnReason}
                            disabled={
                              normalizeReturnReasonLabel(newCustomReturnReason)
                                .length === 0
                            }
                            className="whitespace-nowrap border-transparent bg-[#2563EB] text-white hover:bg-[#1d4ed8] focus:ring-blue-500"
                          >
                            <PlusIcon className="mr-2 h-4 w-4" />
                            Add reason
                          </Button>
                        </div>

                        {customReturnReasons.length > 0 ? (
                          <div className="mt-4 rounded-lg border border-blue-100 bg-white">
                            <div className="divide-y divide-gray-100">
                              {customReturnReasons.map((reason) => {
                                const isEnabled = enabledReturnReasons.includes(
                                  reason.id,
                                );

                                return (
                                  <div
                                    key={reason.id}
                                    className="flex items-center justify-between gap-3 px-4 py-3"
                                  >
                                    <div className="min-w-0 flex-1">
                                      <p className="truncate text-sm font-medium text-gray-900">
                                        {reason.label}
                                      </p>
                                      <p className="mt-0.5 truncate text-xs text-gray-500">
                                        {reason.analyticsKey}
                                      </p>
                                    </div>
                                    <Toggle
                                      isEnabled={isEnabled}
                                      onChange={(nextEnabled) =>
                                        setReturnReasonEnabled(
                                          reason.id,
                                          nextEnabled,
                                        )
                                      }
                                      isSmall
                                      ariaLabel={`${reason.label} return reason`}
                                    />
                                    <button
                                      type="button"
                                      onClick={() =>
                                        handleRemoveCustomReturnReason(
                                          reason.id,
                                        )
                                      }
                                      className="inline-flex shrink-0 items-center justify-center rounded-md border border-gray-200 bg-white p-2 text-gray-400 transition-colors hover:border-red-200 hover:text-red-500"
                                      aria-label={`Remove ${reason.label}`}
                                    >
                                      <TrashIcon className="h-4 w-4" />
                                    </button>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        ) : (
                          <div className="mt-4 rounded-lg border border-white/80 bg-white/80 px-4 py-3 text-sm text-gray-500">
                            No custom reasons yet.
                          </div>
                        )}
                      </div>
                    </div>
                  </CollapsiblePolicySection>

                  <CollapsiblePolicySection
                    id="customer-experience"
                    title="Customer requirements"
                    description="Set notes, photos, and return instructions."
                    summary={customerExperienceSummary}
                    icon={<BookOpenIcon className="h-5 w-5" />}
                    badge={
                      <span className="rounded-full bg-gray-100 px-3 py-1 text-xs font-medium text-gray-600">
                        Customer-facing
                      </span>
                    }
                    isExpanded={expandedPolicySections.customerExperience}
                    onToggle={() => togglePolicySection('customerExperience')}
                  >
                    <div className="p-5 space-y-5">
                      <div className="rounded-lg border border-blue-200 bg-blue-50/40 p-4">
                        <div>
                          <h4 className="text-sm font-semibold text-gray-950">
                            Filing requirements
                          </h4>
                          <p className="mt-1 text-xs leading-relaxed text-gray-600">
                            Set defaults, then override specific reasons.
                          </p>
                        </div>

                        <div className="mt-4">
                          <p className="text-[11px] font-semibold uppercase tracking-[0.08em] text-blue-700">
                            Defaults
                          </p>
                          <div className="mt-3 grid grid-cols-1 gap-3 md:grid-cols-2">
                            <label className="rounded-lg border border-blue-100 bg-white p-3">
                              <span className="block text-sm font-semibold text-gray-900">
                                Note
                              </span>
                              <span className="mt-0.5 block text-xs text-gray-500">
                                Customer context
                              </span>
                              <select
                                aria-label="Default note requirement"
                                value={returnEvidenceDefaults.notes}
                                onChange={(event) =>
                                  updateDefaultEvidenceRequirement(
                                    'notes',
                                    event.target.value as ReturnEvidenceDefault,
                                  )
                                }
                                className="mt-3 w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-900 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-100"
                              >
                                {RETURN_EVIDENCE_DEFAULT_OPTIONS.map(
                                  (option) => (
                                    <option
                                      key={option.value}
                                      value={option.value}
                                    >
                                      {option.label}
                                    </option>
                                  ),
                                )}
                              </select>
                            </label>

                            <label className="rounded-lg border border-blue-100 bg-white p-3">
                              <span className="block text-sm font-semibold text-gray-900">
                                Photos
                              </span>
                              <span className="mt-0.5 block text-xs text-gray-500">
                                Photo evidence
                              </span>
                              <select
                                aria-label="Default photo requirement"
                                value={returnEvidenceDefaults.photos}
                                onChange={(event) =>
                                  updateDefaultEvidenceRequirement(
                                    'photos',
                                    event.target.value as ReturnEvidenceDefault,
                                  )
                                }
                                className="mt-3 w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-900 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-100"
                              >
                                {RETURN_EVIDENCE_DEFAULT_OPTIONS.map(
                                  (option) => (
                                    <option
                                      key={option.value}
                                      value={option.value}
                                    >
                                      {option.label}
                                    </option>
                                  ),
                                )}
                              </select>
                            </label>
                          </div>
                        </div>

                        <div className="mt-4 overflow-hidden rounded-lg border border-gray-200 bg-white">
                          <button
                            type="button"
                            onClick={() =>
                              setShowReasonRequirementOverrides(
                                (current) => !current,
                              )
                            }
                            aria-expanded={showReasonRequirementOverrides}
                            className="flex w-full items-center justify-between gap-4 px-4 py-3 text-left"
                          >
                            <div>
                              <p className="text-sm font-semibold text-gray-950">
                                Reason overrides
                              </p>
                              <p className="mt-0.5 text-xs text-gray-500">
                                Change notes or photos for specific reasons.
                              </p>
                            </div>
                            <div className="flex shrink-0 items-center gap-2">
                              <span className="rounded-full bg-blue-50 px-2.5 py-1 text-xs font-medium text-blue-700">
                                {Object.keys(returnEvidenceOverrides).length}{' '}
                                override
                                {Object.keys(returnEvidenceOverrides).length ===
                                1
                                  ? ''
                                  : 's'}
                              </span>
                              {showReasonRequirementOverrides ? (
                                <ChevronDownIcon className="h-5 w-5 text-gray-400" />
                              ) : (
                                <ChevronRightIcon className="h-5 w-5 text-gray-400" />
                              )}
                            </div>
                          </button>

                          {showReasonRequirementOverrides && (
                            <div className="border-t border-gray-200">
                              <div className="flex items-center justify-between gap-3 bg-gray-50 px-4 py-2.5">
                                <p className="text-xs text-gray-600">
                                  “Use default” inherits the setting above.
                                </p>
                                {Object.keys(returnEvidenceOverrides).length >
                                  0 && (
                                  <button
                                    type="button"
                                    onClick={() => {
                                      markUnsavedChanges();
                                      setReturnEvidenceOverrides({});
                                    }}
                                    className="text-xs font-semibold text-blue-700 hover:text-blue-800"
                                  >
                                    Reset overrides
                                  </button>
                                )}
                              </div>
                              <div className="divide-y divide-gray-100">
                                {visibleReturnReasons.map((reason) => (
                                  <div
                                    key={`${reason.id}-requirements`}
                                    className="grid gap-3 px-4 py-3 md:grid-cols-[minmax(0,1fr)_11rem_11rem] md:items-end"
                                  >
                                    <div className="min-w-0 md:self-center">
                                      <p className="truncate text-sm font-medium text-gray-900">
                                        {reason.title}
                                      </p>
                                      <p className="mt-0.5 text-xs text-gray-500">
                                        {reason.isCustom
                                          ? 'Custom reason'
                                          : 'Preset reason'}
                                      </p>
                                    </div>
                                    <label>
                                      <span className="mb-1 block text-xs font-medium text-gray-600">
                                        Note
                                      </span>
                                      <select
                                        aria-label={`${reason.title} note requirement`}
                                        value={
                                          returnEvidenceOverrides[reason.id]
                                            ?.notes || 'INHERIT'
                                        }
                                        onChange={(event) =>
                                          updateReasonEvidenceRequirement(
                                            reason.id,
                                            'notes',
                                            event.target
                                              .value as ReturnEvidenceOverrideValue,
                                          )
                                        }
                                        className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-100"
                                      >
                                        {RETURN_EVIDENCE_OVERRIDE_OPTIONS.map(
                                          (option) => (
                                            <option
                                              key={option.value}
                                              value={option.value}
                                            >
                                              {option.label}
                                            </option>
                                          ),
                                        )}
                                      </select>
                                    </label>
                                    <label>
                                      <span className="mb-1 block text-xs font-medium text-gray-600">
                                        Photos
                                      </span>
                                      <select
                                        aria-label={`${reason.title} photo requirement`}
                                        value={
                                          returnEvidenceOverrides[reason.id]
                                            ?.photos || 'INHERIT'
                                        }
                                        onChange={(event) =>
                                          updateReasonEvidenceRequirement(
                                            reason.id,
                                            'photos',
                                            event.target
                                              .value as ReturnEvidenceOverrideValue,
                                          )
                                        }
                                        className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-100"
                                      >
                                        {RETURN_EVIDENCE_OVERRIDE_OPTIONS.map(
                                          (option) => (
                                            <option
                                              key={option.value}
                                              value={option.value}
                                            >
                                              {option.label}
                                            </option>
                                          ),
                                        )}
                                      </select>
                                    </label>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      </div>

                      <div>
                        <label
                          htmlFor="return-instructions"
                          className="block text-sm font-semibold text-gray-900"
                        >
                          Return instructions
                        </label>
                        <p className="mt-1 text-xs leading-relaxed text-gray-500">
                          Shown after the label or QR code is ready.
                        </p>
                        <textarea
                          id="return-instructions"
                          value={returnInstructions}
                          onChange={(event) =>
                            handleReturnInstructionsChange(event.target.value)
                          }
                          rows={5}
                          maxLength={600}
                          className="mt-3 w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm leading-6 text-gray-900 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-100"
                        />
                        <p className="mt-2 text-xs text-gray-500">
                          {returnInstructions.length}/600 characters
                        </p>
                      </div>
                    </div>
                  </CollapsiblePolicySection>
                </SettingsSectionGroup>
              </div>

              {/* Sidebar Column */}
              {isSharedPolicy && (
                <div className="space-y-4 2xl:col-span-4">
                  {/* Policy Summary */}
                  <CollapsiblePolicySection
                    id="policy-summary"
                    title="Policy summary"
                    description="Preview the customer and team flow."
                    summary="Customer and team flow."
                    icon={<ShieldCheckIcon className="h-5 w-5" />}
                    badge={
                      <div className="bg-green-100 text-green-800 text-[10px] uppercase font-bold px-2 py-0.5 rounded">
                        Live
                      </div>
                    }
                    isExpanded={expandedPolicySections.policySummary}
                    onToggle={() => togglePolicySection('policySummary')}
                  >
                    <div className="space-y-4 p-5">
                      <div className="rounded-lg bg-blue-50 border border-blue-100 p-4">
                        <div className="flex gap-3">
                          <InformationCircleIcon className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" />
                          <div>
                            <p className="text-[11px] font-semibold uppercase tracking-[0.08em] text-blue-700">
                              Customer flow
                            </p>
                            <p className="text-sm text-blue-900 leading-relaxed mt-1">
                              {policySummaryHeadline}
                            </p>
                          </div>
                        </div>
                      </div>

                      <div className="space-y-2">
                        {policySummaryCards.map((card) => (
                          <div
                            key={card.label}
                            className="rounded-lg border border-gray-200 bg-gray-50 px-4 py-3"
                          >
                            <p className="text-[11px] font-semibold uppercase tracking-[0.08em] text-gray-500">
                              {card.label}
                            </p>
                            <p className="text-sm font-semibold text-gray-900 mt-1">
                              {card.value}
                            </p>
                            <p className="text-xs text-gray-500 leading-relaxed mt-1">
                              {card.description}
                            </p>
                          </div>
                        ))}
                      </div>
                    </div>
                  </CollapsiblePolicySection>

                  <CollapsiblePolicySection
                    id="policy-generator"
                    title="Policy generator"
                    description="Create policy copy for Shopify."
                    summary="Policy copy for Shopify."
                    icon={<BookOpenIcon className="h-5 w-5" />}
                    isExpanded={expandedPolicySections.policyGenerator}
                    onToggle={() => togglePolicySection('policyGenerator')}
                  >
                    <div className="space-y-4 p-5">
                      <div className="rounded-xl bg-blue-50 border border-blue-100 p-4">
                        <p className="text-sm font-semibold text-blue-900">
                          Live preview
                        </p>
                        <p className="text-xs text-blue-700 mt-1 leading-relaxed">
                          {
                            'Shared eligibility and deadlines only. Add your resolution options from Returns and Exchanges before publishing.'
                          }
                        </p>
                      </div>

                      <textarea
                        readOnly
                        aria-label="Shared policy copy"
                        value={policyPreviewText}
                        rows={12}
                        spellCheck={false}
                        className="w-full rounded-xl border border-gray-200 bg-gray-50 px-4 py-4 text-sm leading-6 text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500 resize-y min-h-[280px]"
                      />

                      <div className="flex flex-col gap-2">
                        <Button
                          size={SizeEnum.sm}
                          variant="outlined"
                          disabled={isAiPolishComingSoon}
                          props={{
                            title: 'Polish with AI is coming soon',
                          }}
                          className="justify-center border-gray-200 text-gray-400 bg-gray-50 cursor-not-allowed shadow-none"
                        >
                          AI polish
                          <span className="ml-2 rounded-full bg-white border border-gray-200 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.08em] text-gray-500">
                            Coming Soon
                          </span>
                        </Button>
                        <Button
                          size={SizeEnum.sm}
                          onClick={handleCopyGeneratedPolicy}
                          className="justify-center"
                        >
                          Copy policy
                        </Button>
                      </div>
                    </div>
                  </CollapsiblePolicySection>
                </div>
              )}
            </div>
            )}
          </div>
        </div>
      </SettingsViewWrapper>
    );
  },
);

export default PolicySettings;
